import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'

import i18n from 'i18n'
import conf from 'conf'
import { convert } from 'html-to-text'

import {
  OmForm,
  OmFormItem,
  OmInput,
  OmButton,
  OmRow,
  OmCol,
  OmSelect,
  OmSpinner,
  OmDrawer,
  omMessage,
} from '@owma/kit'
import type { OmFormInstance } from '@owma/kit'

import classNames from 'classnames'
import {
  timeFormat,
  openFile,
  processOpenedFile,
  emailAddress,
  equalFile,
  getSafeHTML,
  getSafeElement,
  getViewText,
  textToHTML,
  getUniqueKey,
  blobToDataUrl,
  detachIFrameImages,
} from 'utils'
import { notImplemented, confirmBase, errorBase, isEmail, formatBytes, ErrorEx } from 'utils/ui'
import {
  convertCidImagesToDocument,
  convertPastedImageSources,
  base64ToBlobFile,
  dataUrlToBlobFile,
  resolveOrCreateDataUrlCid,
} from 'utils/imageProcessor'
import { getDisplayEmailStr } from 'appUtils'

import { IframeHost, TinyMCEAdapter } from 'libs/editor/tinymce'
import {
  NgwEditorAdapter,
  parseBodyDefaultStyle,
  mergeDraftDefaults,
  wrapRegionTextWithDefaultColor,
} from '@owma/common-web/components/editor/ngweditor'
import { takeDraftBodyStyleSkeleton } from '../draftBodyStyleCache'
import { BODY_DOC_FROM_VIEWER, takeParsedBodyDocument } from '../parsedBodyDocCache'
import { buildEditorInitOptions } from '@owma/common-web/components/editor'
import { safeUUID } from 'utils/safeUUID'
import * as Sentry from '@sentry/react'
import jamExt from 'libs/jamUtil/ext'
import type {
  EditorCopyCutEvent,
  EditorDefaultStyle,
  EditorPreProcessEvent,
  HasNativeToolbarResult,
  IEditorEventHandler,
  IMailEditor,
  ToolbarPropsChangePayload,
} from '@owma/common-web/components/editor'
import { editorType } from 'vars'

import {
  ImageToolbar,
  buildImageToolbarLabels,
} from '@owma/common-web/components/editor/ngweditor/ImageToolbar'
import { searchHelper } from 'pages/main/components/EmailForm/SharedEmailSelect'

import dataManager from 'pages/main/data/DataManager'
import { generateEMLUsingEmailObj, validateEMLContent } from 'pages/main/data/JMAPMailbox'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import { selectUser, scheduleSend, emlPreview } from 'popups'
import { isPopupMode } from 'services/popup/isPopupMode'
import type { ExpirationKey } from 'utils/ui/SecureSend'
import { secureSendOptions, calculateExpiredISO } from 'utils/ui/SecureSend'

import chevronDown24 from 'assets/images/toolbar-chevron-down.svg'
import propertyAttachment24 from 'assets/images/property-attachment-gray-24.svg'
import propertySharableLink24 from 'assets/images/property-sharable-link.svg'
import exclamationCircleBlue22 from 'assets/images/exclamation-circle-blue-22.svg'
import exclamationCircleRed22 from 'assets/images/exclamation-circle-red-22.svg'

import SharedEmailSelect from 'pages/main/components/EmailForm/SharedEmailSelect'
import Attachment from 'pages/main/components/Attachment'
import { extendUI } from 'pages/main/components/Attachment/helperUI'

import syncAniGIF from 'assets/images/gif/loading.gif'
import attachFileIcon from 'assets/images/dragdrop-file.svg'

import toolbar from './toolbar'
import editorToolbar from './editorToolbar'
import SecurityWarningBanner from 'utils/ui/SecureSend/SecurityWarningBanner'

import styles from './style.module.scss'
import SchedulingAssistantTab from 'pages/calendar/components/EventEditor/SchedulingAssistantTab'
import AttendeeStatusList from 'pages/calendar/components/EventEditor/SchedulingAssistantTab/AttendeeStatusList'
import { OutlookDateTimeHelper } from 'pages/calendar/modules/outlookdatatime'
import { registerEmailFormDebugHandle, unregisterEmailFormDebugHandle } from 'utils/devTestRegistry'

import { useLargeAttachment, useReadReceipt } from 'vars'

// TypeScript Interfaces
interface TimeRange {
  start: Date
  end: Date
  allday?: boolean
}

interface EmailFormState {
  invisible: boolean
  bccVisible: boolean
  sendDisabled: boolean
  dragover: boolean
  isSending: boolean
  isSelectAvailabilityOpen: boolean
  selectedAvailabilityTimes: TimeRange[]
  editingAvailabilityId: string | null
  attendeeBusyTimes: Record<string, any>
  ownerHasConflict: boolean
  schedulingNavDate: Date
  secureSendEnabled: boolean
  securityWarningVisible: boolean
  secureSendExpired: string | null
  secureSendExpirationKey: ExpirationKey | null
  readReceiptEnabled: boolean
}

interface UserInfo {
  name?: string
  email: string
  [key: string]: any
}

interface EditorFont {
  editorFontFamily: string
  editorFontSizeString: string
  editorFontColorReFwd: string
  editorFontColorNew: string
}

interface EmailFormPropsFromRedux {
  isUnknownAlwaysShowExternalResources: number
  isKnownAlwaysShowExternalResources: number
  editorFont: EditorFont
  signatureForNew: any
  signatureForReplyForward: any
  sendAsAccounts: UserInfo[]
  groupMails: UserInfo[]
  defaultSendAsAccount: UserInfo | null
  confirmBeforeSend: boolean
  alwaysRequestReadReceipt: boolean
  showOriginalMessage: boolean
}

interface EmailFormProps extends EmailFormPropsFromRedux {
  helper?: any
  myWin?: Window
  onSended?: (res: any) => void
  onSetIFrame?: (iframe: HTMLIFrameElement) => void
  onFinishFailed?: (formObj: any) => void
  onCanceled?: () => void
}

/**
 * Subject 필드 input.
 *
 * 안정된 모듈 스코프 컴포넌트로 정의해야 한다. EmailForm.render 안에서 함수 컴포넌트를
 * 재선언하면 매 render 마다 컴포넌트 type 이 달라져 React 가 input 을 unmount/remount 하고,
 * 그 결과 자동 저장(callSave) 이 setState 로 EmailForm 을 리렌더할 때마다 subject input 의
 * 포커스가 사라진다.
 *
 * suffix 갱신 채널 (`registerSetSuffix`) 은 onMount 시 부모(`EmailForm`) 의 인스턴스 슬롯에
 * setter 를 노출 — `EmailForm.setDraftSavedTime` 이 호출하면 저장 시각 텍스트가 갱신된다.
 */
interface SubjectInputProps {
  attrs: any
  inputAttrs: any
  initialSuffix: string | null
  registerSetSuffix: (setter: (s: string | null) => void) => void
  /**
   * Tab 키 (Shift 없음) keydown 시 호출. 호출자는 editor 본문으로 focus 이동 + cursor 위치
   * 설정 책임. callback 호출 전에 `e.preventDefault()` 가 이미 호출된 상태.
   *
   * 본 callback 이 (null/undefined 아닌 함수로) 제공되면 Tab 의 기본 동작 (toolbar 진입) 차단
   * + callback 호출. 제공 안 되면 (또는 callback 안에서 silent return) 기본 동작 유지.
   */
  onTabToEditor?: () => void
}

const SubjectInputEx: React.FC<SubjectInputProps> = ({
  attrs,
  inputAttrs,
  initialSuffix,
  registerSetSuffix,
  onTabToEditor,
}) => {
  const [suffixStr, setSuffixStr] = useState<string | null>(initialSuffix)
  useEffect(() => {
    registerSetSuffix(setSuffixStr)
  }, [registerSetSuffix])

  // subject-tab-to-editor — Tab (Shift 없음) → preventDefault + editor focus callback.
  // inputAttrs.onKeyDown 이 있으면 chain 호출 (현재 EmailForm 의 subject inputAttrs 는 onKeyDown
  // 미전달이라 영향 없음 — 향후 추가 시를 위한 호환성).
  //
  // 표준 IME 패턴: 조합 중(isComposing=true) Tab 은 기본 focus 이동만 막고 아무 동작도 하지 않는다.
  // 조합이 subject 에서 확정된 뒤 도달하는 isComposing=false Tab keydown 에서 비로소 본문으로
  // focus 를 옮긴다.
  //   - Chrome 은 조합 확정 후 Tab keydown 을 isComposing=false 로 한 번 더 발생시킨다. 이때
  //     focus 가 아직 subject 이므로 여기서 소비 → editor 로 Tab 이 흘러들지 않아 tab/들여쓰기 삽입 없음.
  //   - 조합이 subject 에서 끝나므로 미확정 마지막 글자('다')가 본문에 중복 입력되지 않는다.
  //   - 영문(조합 없음)은 첫 Tab 이 곧 isComposing=false → 즉시 이동(기존 동작 동일).
  //   - keyCode===229 는 의도적으로 보지 않는다. IME 를 닫는 마지막 keydown 이 compositionend
  //     이후 isComposing=false 로 오되 keyCode 가 229 로 남는 경우가 있는데(MDN 명시, OS/IME 별
  //     상이), Tab 은 조합을 '여는' 키가 아니라 '닫는' 키라 이 시점엔 이미 확정이 끝난 상태다.
  //     여기서 229 를 조합 중으로 오판하면 정당한 focus 이동을 막아 먹통이 되므로, isComposing
  //     만으로 판별한다.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Tab' && !e.shiftKey && onTabToEditor != null) {
      e.preventDefault()
      if (!e.nativeEvent.isComposing) onTabToEditor()
      return
    }
    inputAttrs.onKeyDown?.(e)
  }

  const suffix = <span className={classNames('draft-time')}>{suffixStr}</span>
  return (
    <OmFormItem {...attrs}>
      <OmInput {...inputAttrs} suffix={suffix} onKeyDown={handleKeyDown} />
    </OmFormItem>
  )
}

const mapStateToProps = ({ settings }: any) => ({
  isUnknownAlwaysShowExternalResources: settings.email.isUnknownAlwaysShowExternalResources,
  isKnownAlwaysShowExternalResources: settings.email.isKnownAlwaysShowExternalResources,
  editorFont: settings.email.editorFont,
  signatureForNew: settings.account?.signatureForNew ?? 'default',
  signatureForReplyForward: settings.account?.signatureForReplyForward ?? 'none',
  sendAsAccounts: settings.localSettings?.email?.sendAsAccounts,
  groupMails: settings.localSettings?.email?.groupMails,
  defaultSendAsAccount: settings.localSettings?.email?.defaultSendAsAccount,
  confirmBeforeSend: settings.email.confirmBeforeSend,
  alwaysRequestReadReceipt: useReadReceipt ? settings.email.alwaysRequestReadReceipt : false,
  showOriginalMessage: settings.email.showOriginalMessage,
})

/**
 * draft-body-style-fidelity: 재열람(draft/resend) body style 시딩 소스의 최소 구조 타입.
 * EmailObj / resendEmail 이 공통으로 제공하는 reqDetail → bodyPart 표면만 사용한다.
 */
interface ReopenBodySource {
  reqDetail: () => Promise<{
    bodyPart?: {
      hasHTMLBody: () => boolean
      getHTMLBody: () => string
    } | null
  } | null>
}

class EmailForm
  extends React.Component<EmailFormProps, EmailFormState>
  implements IEditorEventHandler
{
  state: EmailFormState = {
    invisible: true,
    bccVisible: false,
    sendDisabled: false, // send or save disabled.
    dragover: false,
    isSending: false,
    isSelectAvailabilityOpen: false,
    selectedAvailabilityTimes: [], // Array of {start: Date, end: Date} for multi-selection
    editingAvailabilityId: null, // Track edit mode for availability
    attendeeBusyTimes: {}, // Attendee busy times data from SchedulingAssistantTab
    ownerHasConflict: false, // Owner conflict status from SchedulingAssistantTab
    schedulingNavDate: new Date(), // Navigation date for SchedulingAssistantTab
    secureSendEnabled: false,
    securityWarningVisible: false,
    secureSendExpired: null,
    secureSendExpirationKey: null,
    readReceiptEnabled: false,
  }

  dragover: boolean = false

  // OFM-226: dragover 감지 리스너 등록 대상 캐시 (unmount 해제용). ngw(InlineHost)는 부모
  // document.body 라 명시 해제가 필수이고, IframeHost 는 dispose 후 getDocument() 가 throw
  // 하므로 등록 시점 참조로 해제해야 한다.
  dragOverListenTarget: HTMLElement | null = null

  isEditorInited: boolean = false

  sendAccountList: UserInfo[] = []

  formRef: OmFormInstance | null = null

  toRef: React.RefObject<any> = React.createRef()

  ccRef: React.RefObject<any> = React.createRef()

  bccRef: React.RefObject<any> = React.createRef()

  toolbarRef: React.RefObject<OmFormInstance | null> = React.createRef<OmFormInstance>()

  editorId: string = 'email-editor'

  focusInfo: number = 0

  // 현재 form의 상태를 저장.
  saved: any = {}

  // 서버에 draft가 저장될 때의 form 상태를 저장.
  draftSaved: any = null

  draftSavedAt: string | null = null

  isJMAPUpload: boolean = true
  attachments: any[] = []
  sharingAttachments: any[] = []

  inlineAttachmentMap: Record<string, any> = {}

  srcImageMap: Record<string, any> = {}

  processImageMap: Record<string, any> = {}

  userInfo: UserInfo = {} as UserInfo

  prevFormValues: Record<string, any> = {}

  attachmentUploadProgressMap: Record<string, any> = {}

  editorToolbarElem: any = null

  wrapperRef: React.RefObject<HTMLDivElement> = React.createRef()

  language: string = i18n.language

  // 첨부파일 총 사이즈 제한.
  attachmentsLimit: number = 25 * 1024 * 1024

  // Cache attendees to prevent unnecessary re-renders
  cachedRecipients: any[] = []

  iframe?: HTMLIFrameElement
  // PR-7a-3: <div ref={setEditorContainer}> 를 위한 컨테이너 ref. IframeHost 가 본 div 안에
  // iframe 을 동적으로 생성/마운트한다. JSX 가 inline 모드 (PR-10) 로 바뀌어도 본 컨테이너는
  // 그대로 재사용되며 호출부는 `adapter.lifecycle.init({ mode: 'inline', ... })` 1줄 변경만으로
  // 전환 가능.
  editorContainer?: HTMLDivElement
  // PR-7a-3: 동일 ref 가 unmount → remount 시 init 이 중복 호출되지 않도록 가드.
  editorInitStarted: boolean = false
  // draft-body-style-fidelity: draft/resend 재열람 시 원본 body style 로 시딩한 편집기 기본 스타일.
  // init 에 이 값이 주입되었으면, onReady 경로의 applyEditorSettings 가 사용자 설정으로 덮어쓰지
  // 않도록 동일 값을 재사용한다(setDefaultStyle NoOp → remount 방지). 비-reopen 경로는 null.
  reopenSeededStyle: EditorDefaultStyle | null = null
  // draft-body-style-fidelity: draft/resend 원본 body 에 명시된 글자색(있을 때만). 로드 본문 텍스트를
  // 이 색의 textStyle mark 로 승격(wrapRegionTextWithDefaultColor)하여 커서 위치 색이 toolbar 에
  // 반영되도록 한다. body 에 color 가 없던 draft 는 null → 승격하지 않음(불필요 데이터 변경 방지).
  reopenBodyColor: string | null = null
  // draft-body-style-from-viewer 활성 플래그 — false 면 기존 전체 파싱 시딩으로 즉시 원복.
  private static readonly BODY_STYLE_FROM_VIEWER = true
  // draft-reopen-parse-share: initEditor(reopen seeding)가 파싱한 Document 를 보관해 draft/resend
  // 본문 적용(convertCidNgwToDocument)에서 재사용 — 동일 2.7MB 문자열의 중복 DOMParser 파싱
  // (WebKit ~4.2s) 제거. 소비(take) 시 비우며, 전달 후 Document 는 cid 변환이 mutate 하고
  // setDataFromDOM/getSafeElement 가 소진한다. unmount 시 해제.
  private reopenParsedDoc: Document | null = null
  private reopenParsedHTML: string | null = null
  // draft-body-style-fidelity: initEditor 의 async reqDetail await 도중 unmount 되었는지 신뢰 가능
  // 판정용. componentWillUnmount 에서 true → await 재개 후 detached 상태 lifecycle.init 방지(누수 차단).
  isUnmounted: boolean = false
  attachmentHelper?: any
  isAllowedEmail?: boolean
  emailInfo?: any
  domain?: string
  initialValues?: any
  editElem?: React.ReactElement
  setFromInfo?: (info: UserInfo) => void
  setDraftSavedTimeStr?: (str: string) => void
  draftSavedTimeStr?: string
  titleChangeTimer?: NodeJS.Timeout
  curValues?: any
  formInfo?: any

  // PR-7a-2 (~PR-N7): 어댑터 인스턴스. setEventHandler(this) 만 호출되며 lifecycle.init 은
  // PR-7a-3 에서 활성화. PR-N7 부터는 vars.editorType 분기로 TinyMCEAdapter / NgwEditorAdapter
  // 중 선택. 두 어댑터 모두 IEditor + IMailEditor 인터페이스를 동일하게 구현하므로 호출부는
  // 어댑터 종류와 무관 (IMailEditor 추상 타입으로 보관).
  editorAdapter: IMailEditor = editorType === 'ngw' ? new NgwEditorAdapter() : new TinyMCEAdapter()

  // PR-7a-2: editor.on('copy cut') 의 superseded guard. 이전 editorExt.ts L462 의
  // copySeq 클로저를 EmailForm 멤버로 흡수.
  copySeq: number = 0

  // PR-7a-2: editorExt.ts uniqueBlobId 클로저 카운터 흡수.
  blobIdCounter: number = 0

  // reply-editor-quoted-lazy-bake: 인용 본문 백그라운드 bake 의 완료 promise (OMD-131
  // bakeFullBody 래치 패턴). null = 인용 없음/완료. send·save·EML preview 는
  // ensureQuotedBaked() 로 이 promise 를 await 해 인용 누락을 막는다.
  private quotedBakePromise: Promise<void> | null = null
  // unmount/onReady 재진입 시 진행 중 bake 를 중단한다(styleToInlineStyleAsync 청크 경계
  // 에서 AbortError). 재진입 시엔 abort 후 새 controller 로 교체(재무장)한다.
  private quotedBakeController = new AbortController()

  async componentDidMount() {
    this.replyPerfMark('form:mount')
    i18n.on('languageChanged', this.onLanguageChanged)

    this.setHelper()

    if (!__ENV_PRODUCTION__) {
      registerEmailFormDebugHandle(this)
    }

    // PR-7a-2: 어댑터에 EmailForm 을 IEditorEventHandler 로 등록. lifecycle.init 은
    // PR-7a-3 시점에 호출되므로 본 단계에서는 forward path 가 활성화되지 않는다.
    // 단, NgwEditor 등 다른 어댑터로 바꾸어도 동일한 IEditorEventHandler 표면을 통해
    // 통합되도록 미리 핸들러 등록만 수행.
    this.editorAdapter.lifecycle.setEventHandler(this)

    // 'ngw' 모드는 NgwEditor 가 자체 toolbar 를 내장하므로 EmailForm 의 자체 editorToolbar 렌더 생략.
    // TinyMCE 모드는 iframe 안 editor 가 toolbar 를 갖지 않아 EmailForm 이 직접 그린다.
    if (editorType !== 'ngw') {
      this.editorToolbarElem = this.createEditorToolbarElem()
    }

    this.forceUpdate()
  }

  componentDidUpdate(prevProps: EmailFormProps) {
    const { helper } = this.props

    if (helper !== prevProps.helper) {
      this.setHelper()
    }
  }

  componentWillUnmount() {
    // draft-reopen-parse-share: 보관 중인 파싱 Document 해제(2.7MB DOM 참조 제거).
    this.reopenParsedDoc = null
    this.reopenParsedHTML = null
    const { myWin } = this.props

    // draft-body-style-fidelity: initEditor 의 async reqDetail await 재개 후 detached 상태에서
    // lifecycle.init 이 실행되어 editor root/host 가 정리 없이 누수되는 것을 막는 신호.
    this.isUnmounted = true

    // reply-editor-quoted-lazy-bake: 진행 중 인용 bake 중단 — 폼 닫은 뒤 뒤늦은 변환이
    // 리소스를 쓰지 않게 한다.
    this.quotedBakeController.abort()

    if (!__ENV_PRODUCTION__) {
      unregisterEmailFormDebugHandle(this)
    }

    i18n.off('languageChanged', this.onLanguageChanged)

    // OFM-226: dragover 감지 리스너 해제. 반드시 lifecycle.dispose() 전에 수행 —
    // IframeHost 는 dispose(teardown)가 iframe 을 파괴한 뒤 getDocument() 가 throw 하므로
    // 등록 시점에 캐시한 dragOverListenTarget 으로 해제한다. ngw(InlineHost)는 부모
    // document.body 에 등록되므로 미해제 시 compose 반복 open/close 로 리스너가 누적되고
    // unmount 후 setState 가 호출된다.
    if (this.dragOverListenTarget != null) {
      this.dragOverListenTarget.removeEventListener('dragover', this.onEditorFileDragOver)
      this.dragOverListenTarget = null
    }

    // if (this.observerObj != null) {
    //   this.observerObj.clear()
    // }

    if (this.editorAdapter?.lifecycle.isReady() === true) {
      // PR-7b: editorMgr.observerObj.clear() → editor.state.clearObservers() 어댑터 호출.
      // 어댑터 구현은 editorMgr 초기화 전 / 이미 정리된 상황을 silent 처리한다.
      this.editorAdapter.state.clearObservers()
    }

    // 에디터 iframe의 inline img(blob/data/cid)가 detached 문서를 active로 잡지 않도록
    // img만 분리한다. lifecycle.dispose()가 iframe DOM을 제거하므로 그 전에 수행한다.
    // about:blank 네비게이션은 하지 않는다 — editorWindow의 WindowProxy가 교체되면
    // 어댑터의 비동기 .then 체인(reqDetail/getSafeHTML/download 후 setData 등)이 unmount 후
    // resolve될 때 throw하기 때문. img 분리만으로 active 문서 보존(누수)은 차단된다.
    detachIFrameImages(this.iframe)

    // PR-7a-3: setEditorContainer 에서 호출한 lifecycle.init 의 dispose. IframeHost teardown
    // (iframe DOM 제거 — div 가 React 에 의해 곧 detach 되므로 사실상 idempotent) + parent
    // 의 `initEditorNative` 트램폴린 복원을 수행. init 이 실패했거나 시작되지 않은 경우에도
    // dispose 는 안전 (host == null 가드).
    if (this.editorInitStarted === true) {
      try {
        this.editorAdapter.lifecycle.dispose()
      } catch (err) {
        console.error('EmailForm: editor lifecycle.dispose failed', err)
      }
    }

    this.iframe = undefined

    Object.values(this.processImageMap).forEach((value) => {
      value.infoList.forEach((info) => {
        if (info != null && info.url != null) {
          const curWindow = (myWin || window) as Window & typeof globalThis
          curWindow.URL.revokeObjectURL(info.url)
        }
      })
    })

    // 첨부파일 삭제 시, 해당 첨부파일의 URL을 삭제.
    if (this.attachments != null && this.attachments.length > 0) {
      this.attachments.forEach((attachment) => {
        if (attachment != null && attachment.clearURL != null) {
          attachment.clearURL()
        }
      })
    }
    Object.values(this.inlineAttachmentMap).forEach((attachment) => {
      if (attachment != null && attachment.clearURL != null) {
        attachment.clearURL()
      }
    })
  }

  getDebugRootElement = (): HTMLDivElement | null => {
    return this.wrapperRef.current
  }

  getDebugState = (): { isSending: boolean; invisible: boolean } => {
    const { isSending, invisible } = this.state
    return {
      isSending,
      invisible,
    }
  }

  getInitialValues = (): any => {
    if (this.initialValues == null) {
      const { helper, defaultSendAsAccount } = this.props

      let initialValues: any = {}

      if (helper != null) {
        this.userInfo = helper.getUserInfo()
        if (this.userInfo != null) {
          if (defaultSendAsAccount != null && defaultSendAsAccount.email != null) {
            initialValues.from = defaultSendAsAccount.email
          } else {
            initialValues.from = this.userInfo.email
          }

          this.domain = `@${emailAddress(this.userInfo.email).host}`
        }

        const extValues = helper.getExtValues()
        if (extValues != null) {
          // resendEmail / attachmentFiles 는 폼 필드가 아니라 setEmailData 에서
          // helper.getExtValues() 로 별도 소비되는 값이다. 특히 resendEmail 은 ThreadView
          // InnerViewer 로의 순환참조(reactObjList)를 가진 원본 EmailObj 라, 폼 store
          // (RHF defaultValues)에 유입되면 getFieldsValue()=form.getValues() 가 이를 반환해
          // saveFormValues 의 JSON.stringify 에서 "circular structure" 에러가 발생한다.
          const formExtValues = { ...extValues }
          delete formExtValues.resendEmail
          delete formExtValues.attachmentFiles
          initialValues = {
            ...initialValues,
            ...formExtValues,
          }
        }

        // prevEmail 정보는 직접 사용하지 않음.
        // const prevEmail = helper.getPrevEmail()
        // if (prevEmail != null && prevEmail.data != null) {
        //   const { subject, cc, bcc, to } = prevEmail.data

        //   if (subject != null) {
        //     initialValues.subject = subject
        //   }
        //   if (cc != null) {
        //     initialValues.cc = cc
        //   }
        //   if (bcc != null) {
        //     initialValues.bcc = bcc
        //   }
        //   if (to != null) {
        //     initialValues.to = to
        //   }
        // }

        const draftEmail = helper.getDraftEmail()
        if (draftEmail != null && draftEmail.data != null) {
          const { subject, from, cc, bcc, to } = draftEmail.data

          if (from != null) {
            initialValues.from = from.email != null ? from.email : from
          }

          if (subject != null) {
            initialValues.subject = subject
          }
          if (cc != null) {
            initialValues.cc = cc
          }
          if (bcc != null) {
            initialValues.bcc = bcc
          }
          if (to != null) {
            initialValues.to = to
          }
        }
      }

      this.initialValues = initialValues
    }

    return this.initialValues
  }

  setFormRef = (ref: any) => {
    if (ref != null) {
      this.formRef = ref
      // if (this.formRef != null && this.initialValues != null) {
      //   this.setFormValues(this.initialValues)
      // }
    }
  }

  onLanguageChanged = (language: string) => {
    this.language = language
    this.applyLanguage()

    if (this.editorAdapter?.lifecycle.isReady() === true) {
      // PR-7b: setShowQuotedButtonText → mail.region.quotedButton.setText 어댑터 호출.
      this.editorAdapter.mail.region.quotedButton.setText(i18n.t('editor.button.showQuoted'))
    }

    // 'ngw' 모드: 자체 editorToolbar 미사용 (NgwEditor 내장 toolbar 사용).
    if (editorType !== 'ngw') {
      this.editorToolbarElem = this.createEditorToolbarElem()
    }
    this.forceUpdate()
  }

  applyLanguage = () => {
    // PR-7c: editorWindow.tinymce.util.I18n.setCode → adapter.lifecycle.setLocale.
    // 어댑터가 init 전이거나 iframe tinymce 글로벌이 미준비면 내부 silent no-op.
    const lang = this.language
    const locale: 'ko-KR' | 'ja-JP' | 'en-US' =
      lang === 'ko-KR' ? 'ko-KR' : lang === 'ja-JP' ? 'ja-JP' : 'en-US'
    this.editorAdapter.lifecycle.setLocale(locale)
  }

  setAttendeeBusyTimes = (attendeeBusyTimes: Record<string, any>) => {
    // 시간표 편집 팝업의 SchedulingAssistantTab 이 매번 새 객체 reference 로
    // 호출할 수 있어, 동일 내용일 때 setState 를 skip 하여 무한 cascade 차단.
    const prev = this.state.attendeeBusyTimes
    if (prev === attendeeBusyTimes) return
    const prevKeys = Object.keys(prev || {})
    const nextKeys = Object.keys(attendeeBusyTimes || {})
    if (prevKeys.length === 0 && nextKeys.length === 0) return
    this.setState({
      attendeeBusyTimes: attendeeBusyTimes,
    })
  }

  setOwnerHasConflict = (ownerHasConflict: boolean) => {
    if (this.state.ownerHasConflict === ownerHasConflict) return
    this.setState({
      ownerHasConflict: ownerHasConflict,
    })
  }

  setHelper = () => {
    const { helper } = this.props

    if (helper != null) {
      // upload, download 중에 window가 닫히면, 해당 window에 포함된 process가 종료되므로,
      // parent window에서 생성된 upload, download function을 호출해야 함.
      this.attachmentHelper = helper.attachmentHelper

      helper.formAPIs = {
        isEmpty: () => {
          return this.isEmpty()
        },
        isModified: () => {
          return this.isModified(this.saved)
        },
        callCancel: () => {
          this.cancel()
        },
        callSend: () => {
          this.callSend()
        },
        callSave: () => {
          this.callSave()
        },
        callScheduleSend: () => {
          this.callScheduleSend()
        },
        getEmailValues: (forSave) => {
          // console.log(' - getEmailValues - ')
          return this.getEmailValues(forSave)
        },
        getNineBodyId: () => {
          // PR-7b: editorMgr.nineBodyID → mail.getNBodyID 어댑터 호출.
          if (this.editorAdapter?.lifecycle.isReady() === true) {
            return this.editorAdapter.mail.getNBodyID()
          }
          return null
        },
      }
    }
  }

  // popup(handoff-only)에서 opener 가 닫히면 sendHelper.dataMgr(=opener realm 참조)가 죽어
  // 발송/저장이 예외로 실패한다. 시도 전에 중단해 작성 내용 유실을 막고 사용자에게 안내한다.
  // 메인 앱(inline 에디터)에서는 isPopupMode()=false 라 항상 통과한다.
  private isPopupOpenerLost = (): boolean => {
    if (!isPopupMode()) {
      return false
    }
    const opener = window.opener as Window | null
    return opener == null || opener.closed === true
  }

  private notifyOpenerLost = (): void => {
    errorBase({
      title: i18n.t('email.popup.openerLost.sendBlockedTitle'),
      content: i18n.t('email.popup.openerLost.sendBlockedContent'),
    })
  }

  callSend = () => {
    const { helper, onSended, confirmBeforeSend } = this.props

    // popup opener 종료 시 발송 불가 → 내용 보존을 위해 중단하고 안내.
    if (this.isPopupOpenerLost()) {
      this.notifyOpenerLost()
      return
    }

    // console.log(' - callSend - start ');
    // this.saveFormValues()

    let invalidEmails = []

    const checkInvalidEmails = (emailInfos) => {
      if (emailInfos != null && emailInfos.length > 0) {
        const list = emailInfos.filter((item) => {
          const email = item.email || item
          if (email != null && typeof email === 'string' && email.trim().length > 0) {
            return isEmail(email) === false
          }
          return true
        })
        if (list.length > 0) {
          invalidEmails = [...invalidEmails, ...list]
        }
      }
    }

    // 검증에는 본문/인라인 첨부가 불필요하므로 getEmailValues(getDataPreprocessed→onPreProcess 로
    // inlineAttachmentMap 재구축하는 부수효과 동반)를 호출하지 않고 폼 값 + 사용자 첨부만 사용한다.
    // 실제 본문/인라인 첨부는 이후 send→getEmailValues 에서 재구축된다(중복 처리 제거).
    const { to, cc, bcc, subject } = this.getFormValues(false)
    const attachments = this.attachments
    const sharingAttachments = this.sharingAttachments

    console.log(' - callSend : ', to, subject)

    const warning = (options) => {
      confirmBase({
        wrapClassName: classNames(styles.editorConfirm),
        icon: (
          <img src={exclamationCircleRed22} style={{ paddingRight: '16px' }} alt="exclamation" />
        ),
        title: options.title,
        content: options.content,
        okText: i18n.t('common.button.ok'),
        onOk: () => {
          this.callSave()
        },
        width: 380,
        footer: (_, { OkBtn }) => (
          <div className={classNames(styles.footer)}>
            <div className={classNames('btns')}>
              <OkBtn />
            </div>
          </div>
        ),
      })
    }

    if (to == null || to.length === 0) {
      warning({
        title: i18n.t('editor.title.recipientError'),
        content: i18n.t('editor.error.recipientError'),
      })
      return
    }

    const isUploading = [...(attachments || []), ...(sharingAttachments || [])].some(
      (attachment) => {
        const { isUploadProgressing, isUploading } = attachment
        return isUploadProgressing === true || isUploading === true
      },
    )

    if (isUploading === true) {
      warning({
        title: i18n.t('editor.title.attachmentUploading'),
        content: i18n.t('editor.error.attachmentUploading'),
      })
      return
    }

    checkInvalidEmails(to)
    checkInvalidEmails(cc)
    checkInvalidEmails(bcc)

    if (invalidEmails.length > 0) {
      const emails = invalidEmails.join(', ')
      warning({
        title: i18n.t('editor.title.invalidEmail'),
        content: i18n.t('editor.error.invalidEmail', { emails }),
      })
      return
    }

    const send = async () => {
      if (helper != null) {
        this.setSendBtnDisabled(true)

        // console.log(' - callSend - start ');

        if (this.editorAdapter?.lifecycle.isReady() === true) {
          // PR-7b: send 실행 시, noInputTimer를 clear. editorMgr.clearNoInputTimer → state.clearNoInputTimer.
          this.editorAdapter.state.clearNoInputTimer()
        }

        // isSending 은 await 이전에 동기 설정해야 callSend 직후 상태가 즉시 반영된다.
        this.setState({
          isSending: true,
        })

        // editor-image-tools — 전송 직전 회전 이미지 픽셀화(비파괴 캐시). 이후 동기
        // getEmailValues/helper.save/helper.send 가 캐시를 소비해 실제 회전된 픽셀로 전송.
        await this.editorAdapter.image.prepareForSend()

        // reply-editor-quoted-lazy-bake: 인용 백그라운드 bake 가 끝나기 전에 직렬화하면
        // 전송 본문에 인용이 빠진다 — 완료를 기다린 뒤 getEmailValues/helper.save 진행.
        await this.ensureQuotedBaked()

        // const draftEmail = helper.getDraftEmail();
        // if (draftEmail == null) {
        const sendValues = this.getEmailValues(false)
        console.log('[DEBUG send htmlBody]', sendValues != null ? sendValues.htmlBody : null)
        helper.save(true, false)
        // }

        let sendPromise = null
        if (this.state.secureSendEnabled && this.state.secureSendExpirationKey != null) {
          // 만료일은 발송 시점 기준으로 계산한다. 드래프트 복원 시 계산된 만료일은 UI 표시용이며,
          // 실제 발송에는 사용하지 않는다.
          const expiredDate = calculateExpiredISO(this.state.secureSendExpirationKey)
          sendPromise = helper.sendSecure(expiredDate, this.state.readReceiptEnabled)
        } else if (this.state.readReceiptEnabled) {
          sendPromise = helper.sendReadReceipt()
        } else {
          sendPromise = helper.send()
        }

        sendPromise
          .then((res) => {
            if (onSended != null) {
              onSended(res)
            }
            this.updateSendBtnByUploadStatus()
            if (this.state.secureSendEnabled || this.state.readReceiptEnabled) {
              // 보안 메일 / 수신확인 메일 모두 별도 API로 발송되므로 JMAP draft가 잔존하지 않도록 정리
              this.deleteDraft(true)
            }
          })
          .catch((err) => {
            if (err instanceof ErrorEx) {
              const msgNode = err.msgNode
              errorBase({
                title: 'Error',
                content: msgNode,
              })
            } else {
              const message =
                err.name === 'AbortError' ? i18n.t('email.result.sendTimeout') : err.message
              errorBase({
                title: 'Error',
                content: message,
              })
            }
            this.updateSendBtnByUploadStatus()
          })
          .finally(() => {
            this.setState({
              isSending: false,
            })
            // editor-image-tools — 전송 흐름 종료(성공/실패) 시 픽셀화 캐시 무효화.
            this.editorAdapter.image.clearPixelizeCache()
          })
      }
    }

    const confirmSend = () => {
      if (confirmBeforeSend === true) {
        // 발송 모드별 Title 분기 (일반 / 보안 메일 / 수신확인). content는 동일.
        const isSecureSendActive =
          this.state.secureSendEnabled && this.state.secureSendExpirationKey != null
        const isReadReceipt = this.state.readReceiptEnabled
        const title = isSecureSendActive
          ? i18n.t('email.title.confirmSendMessageSecure')
          : isReadReceipt
            ? i18n.t('email.title.confirmSendMessageReadReceipt')
            : i18n.t('email.title.confirmSendMessage')
        confirmBase({
          title,
          content: i18n.t('email.desc.confirmSendMessage'),
          icon: <img src={exclamationCircleBlue22} alt="exclamation" />,
          okText: i18n.t('common.button.yes'),
          cancelText: i18n.t('common.button.no'),
          width: 480,
          onOk: () => {
            send()
          },
        })
      } else {
        send()
      }
    }

    if (subject == null || subject.trim().length === 0) {
      confirmBase({
        wrapClassName: classNames(styles.editorConfirm),
        icon: (
          <img src={exclamationCircleRed22} style={{ paddingRight: '16px' }} alt="exclamation" />
        ),
        title: 'Warning',
        content: (
          <div>
            {i18n.t('editor.title.noSubject')}
            <br />
            {i18n.t('editor.warning.noSubject')}
          </div>
        ),
        okText: i18n.t('editor.button.noSubjectSend'),
        cancelText: i18n.t('common.button.cancel'),
        onOk: () => {
          confirmSend()
        },
        width: 480,
      })
    } else {
      confirmSend()
    }
  }

  callSave = (forced: boolean = false) => {
    const { helper } = this.props

    // popup opener 종료 시 저장 불가. 수동(forced) 저장일 때만 안내하고, 자동 저장은 조용히 스킵한다.
    if (this.isPopupOpenerLost()) {
      if (forced === true) {
        this.notifyOpenerLost()
      }
      return
    }

    // console.log(' - callSave - start ');
    const saved = this.saved
    if (forced === true || this.isModified(saved) === true) {
      // reply-editor-quoted-lazy-bake: 인용 bake 완료 전 저장하면 draft 에 인용이 빠진다 —
      // 완료를 기다린 뒤 진행(백그라운드 선출발이라 대기는 드물고 짧다). opener lost /
      // isModified 판정(early return)은 위에서 동기 유지. 호출부는 전부 fire-and-forget.
      void this.ensureQuotedBaked().then(() => {
        if (helper != null) {
          this.setSendBtnDisabled(true)

          if (this.editorAdapter?.lifecycle.isReady() === true) {
            // PR-7b: 수동 save 실행 시 noInputTimer clear → state.clearNoInputTimer.
            this.editorAdapter.state.clearNoInputTimer()
          }

          const onEnd = () => {
            if (this.editorAdapter?.lifecycle.isReady() === true) {
              // PR-7b: save 완료시 noInputTimer 재시작 → state.checkNoInput.
              this.editorAdapter.state.checkNoInput()
            }
            this.updateSendBtnByUploadStatus()
          }

          helper
            .save(forced)
            .then(({ mailData, prevDraft, draft }) => {
              // save가 성공하면 form의 상태를 저장.
              this.saveFormValues()
              console.log(' - saved - ', mailData, prevDraft, draft)

              if (draft != null) {
                // 저장된 draft의 receivedAt을 적용.
                this.setDraftSavedTime(draft.data.receivedAt)
              }

              // console.log(' - callSave - end ');
              onEnd()
            })
            .catch((err) => {
              console.log(' - callSave - err : ', err)
              onEnd()
            })
        } else {
          this.saveFormValues()
        }
      })
    }
  }

  callSaveLocal = () => {
    const { helper } = this.props

    if (helper != null) {
      this.setSendBtnDisabled(true)

      if (this.editorAdapter?.lifecycle.isReady() === true) {
        // PR-7b: 수동 save 실행 시 noInputTimer clear → state.clearNoInputTimer.
        this.editorAdapter.state.clearNoInputTimer()
      }

      const onEnd = () => {
        if (this.editorAdapter?.lifecycle.isReady() === true) {
          // PR-7b: save 완료시 noInputTimer 재시작 → state.checkNoInput.
          this.editorAdapter.state.checkNoInput()
        }
        this.updateSendBtnByUploadStatus()
      }

      // reply-editor-quoted-lazy-bake: 인용 bake 완료 후 저장해야 로컬 draft 에 인용이 포함된다.
      void this.ensureQuotedBaked().then(() =>
        helper
          .saveLocal()
          .then(() => {
            onEnd()
          })
          .catch((err) => {
            console.log(' - callSaveLocal - err : ', err)
            onEnd()
          }),
      )
    }
  }

  setSendBtnDisabled = (disabled: boolean) => {
    this.setState({
      sendDisabled: disabled === true,
    })
  }

  updateSendBtnByUploadStatus = () => {
    const hasUploading = [...this.attachments, ...this.sharingAttachments].some((attachment) => {
      return attachment.isUploadProgressing === true || attachment.isUploading === true
    })
    this.setSendBtnDisabled(hasUploading)
  }

  checkAllowedAndKnownSender = (
    sender: string,
    emailId: string | null,
    onResult: (result: boolean) => void,
  ) => {
    const { helper, isUnknownAlwaysShowExternalResources, isKnownAlwaysShowExternalResources } =
      this.props

    const dataMgr = helper.dataMgr || dataManager

    if (emailId != null && dataMgr.isTempAllowedEmailId(emailId) === true) {
      onResult(true)
      return
    }

    // allowed email 과 known email은 다르게 처리해야 함.
    dataMgr
      .checkAllowedSender(sender)
      .then((result) => {
        console.log(' --- checkAllowedSender : ', result)
        this.isAllowedEmail = result

        if (result !== true) {
          // 잠복 race 수정(reply-editor-quoted-lazy-bake C1): 종전에는 checkKnownSender(비동기)
          // 완료를 기다리지 않고 onResult 를 먼저 호출해, isKnown/isUnknownAlwaysShow
          // ExternalResources 설정이 결과에 반영되지 않았다. 조회 완료 후 통지하도록 이동.
          // return 으로 체인에 연결해 checkKnownSender reject 도 아래 .catch 가 잡게 한다.
          return dataMgr.checkKnownSender(sender).then((isKnownEmail) => {
            if (isKnownEmail === true) {
              onResult(isKnownAlwaysShowExternalResources === 0)
            } else {
              onResult(isUnknownAlwaysShowExternalResources === 0)
            }
          })
        }

        onResult(result)
      })
      .catch((err: unknown) => {
        // 발신자 allowed/known 조회가 실패해도 onResult 를 반드시 1회 호출한다. 콜백 기반이라
        // 미호출 시 호출부(reply bake 수렴점 Promise.all / resend 로딩 체인)가 영구 교착되어
        // "편집 개방이 영영 안 열림"/overlay 잔존을 유발한다. 외부 리소스는 보수적으로 차단(false).
        console.error(' - checkAllowedAndKnownSender error - ', err)
        onResult(false)
      })
  }

  getEmailInfo = (email: string): any => {
    let ret = this.emailInfo?.map?.[email]
    if (ret == null) {
      ret = {
        email,
      }
    }
    return ret
  }

  getContainer = () => {
    const { myWin } = this.props
    const container = myWin != null ? myWin.document.body.firstElementChild : document.body
    return container
  }

  getFromOption = (name: string | undefined, email: string) => {
    return {
      label: getDisplayEmailStr({ name, email }, true),
      value: email,
    }
  }

  createFromSelect = (inputAttrs: any, info: any, infoFunc: any, formItemAttrs: any) => {
    const { helper, sendAsAccounts, groupMails } = this.props
    const userInfo = helper.getUserInfo()

    this.sendAccountList = [userInfo]

    if (sendAsAccounts != null && sendAsAccounts.length > 0) {
      this.sendAccountList = [userInfo, ...sendAsAccounts]
    }

    if (groupMails != null && groupMails.length > 0) {
      this.sendAccountList = [...this.sendAccountList, ...groupMails]
    }

    const options = this.sendAccountList.map((account) => {
      return this.getFromOption(account.name, account.email)
    })

    const prevInputAttrs = {
      ...inputAttrs,
    }

    if (this.sendAccountList.length === 0) {
      prevInputAttrs.suffixIcon = null
      prevInputAttrs.disabled = true
    }

    const newInputAttrs = {
      className: classNames(styles.fromSelect),
      ...prevInputAttrs,
      popupMatchSelectWidth: false,
      popupClassName: classNames('contextMenu', styles.emailSelectPopup),
      getPopupContainer: () => {
        return this.getContainer()
      },
      options: options,
    }

    return (
      <OmFormItem key={formItemAttrs.name} {...formItemAttrs}>
        <OmSelect {...newInputAttrs} />
      </OmFormItem>
    )

    // const self = this

    // const getEmailText = (user) => {
    //   const text = getDisplayEmailStr({ ...user }, true)
    //   return text
    // }

    // const FromText = () => {
    //   const [info, setInfo] = useState(userInfo)

    //   const text = getEmailText(info)

    //   useEffect(() => {
    //     self.setFromInfo = (uInfo) => {
    //       setInfo(uInfo)
    //     }
    //   }, [])

    //   return <div className={classNames(styles.fromSelect)}>{text}</div>
    // }

    // return (
    //   <OmFormItem {...formItemAttrs}>
    //     <FromText />
    //   </OmFormItem>
    // )
  }

  // createEmailSelect = (name, inputAttrs, info, infoFunc, formItemAttrs) => {
  //   const reduxState = reduxStore.getState()

  //   const { user } = reduxState

  //   const options = info != null ? info.options || [...this.emailOptions] : [...this.emailOptions]

  //   const myEmail = options.find((item) => {
  //     return item.value === user.email
  //   })

  //   if (myEmail == null) {
  //     options.push(this.getToOption(user.name, user.email))
  //   }

  //   // const myEmailObj = emailAddress(user.email);

  //   const newInputAttrs = {
  //     ...inputAttrs,
  //     notFoundContent: null,
  //     getPopupContainer: () => {
  //       return this.getContainer()
  //     },
  //     labelRender: (option) => {
  //       const emaildata = this.getEmailInfo(option.value)
  //       return getDisplayEmailStr(emaildata)
  //     },
  //     popupClassName: classNames(styles.emailSelectPopup),
  //     onSearch: (text) => {
  //       console.log(' - onSearch : ', text)
  //       // console.log(text);
  //       // let className = classNames(styles.notEmail);;
  //       if (isEmail(text) === true) {
  //         const opt = options.find((option) => {
  //           return option.value === text
  //         })

  //         // if (text.endWith(`@${myEmailObj.host}`) === true) {
  //         //   className = classNames(styles.innerEmail);
  //         // } else {
  //         //   className = classNames( styles.outerEmail);
  //         // }

  //         if (opt == null) {
  //           const newOptions = [this.getToOption(text, text), ...options]

  //           this.setState(infoFunc(newOptions))
  //         }
  //       }
  //     },
  //     // options: options,
  //   }

  //   const self = this

  //   const selectData = {}

  //   const SelectEx = (props) => {
  //     const [optionsHide, setOptionsHide] = useState(false)
  //     const [selectedItems, setSelectedItems] = useState([])
  //     const [exOptions, setExOptions] = useState([...options])
  //     const filteredOptions = exOptions.filter((o) => {
  //       const res = selectedItems.find((item) => {
  //         return item === o.value
  //       })
  //       // console.log(' - filter res : ', res, o);
  //       return res == null
  //     })

  //     console.log(' - selectedItems : ', selectedItems)
  //     console.log(' - filteredOptions : ', filteredOptions)

  //     const inputAttrsEx = {
  //       ...newInputAttrs,
  //       popupClassName: classNames(styles.emailSelectPopup, { hidden: optionsHide }),
  //       value: selectedItems,
  //       onChange: (value) => {
  //         console.log(' - onChange : ', value)
  //         setSelectedItems(value)
  //         setExOptions([])
  //       },
  //       options: filteredOptions,
  //       onSearch: (text) => {
  //         console.log(' - onSearch : ', text)

  //         selectData.text = text

  //         // if (isEmail(text) === true) {
  //         if (text.length >= 0) {
  //           const opts = options.filter((option) => {
  //             return text.length === 0 || option.value.indexOf(text) > -1
  //           })

  //           if (opts == null || opts.length === 0) {
  //             // enterkey 입력 시 메뉴에 항목이 없으면 추가되지 않으므로, 보이지 않게 추가.
  //             const newOptions = [this.getToOption(text, text)]
  //             setExOptions(newOptions)
  //             setOptionsHide(true)
  //           } else {
  //             setExOptions([...opts])
  //             setOptionsHide(false)
  //           }
  //         }
  //       },
  //       onInputKeyDown: (e) => {
  //         console.log(' - onInputKeyDown : ', e.keyCode, e.key)
  //         if (e.keyCode === 32 || e.keyCode === 186 || e.keyCode === 188) {
  //           if (selectData.text != null && selectData.text.trim().length > 0) {
  //             if (selectedItems.indexOf(selectData.text) < 0) {
  //               const newSelected = [...selectedItems, selectData.text]
  //               setSelectedItems(newSelected)

  //               if (self.formRef.current != null) {
  //                 self.formRef.current.setFieldsValue({ [name]: newSelected })
  //                 setExOptions([])
  //                 setOptionsHide(true)
  //               }
  //             }
  //           }
  //         }
  //       },
  //     }

  //     const selectElem = <OmSelect {...inputAttrsEx} />

  //     if (formItemAttrs != null) {
  //       return <OmFormItem {...formItemAttrs}>{selectElem}</OmFormItem>
  //     }

  //     return selectElem
  //   }

  //   return <SelectEx key={formItemAttrs.name} />

  //   // const selectElem = (
  //   //   <Select
  //   //     {...newInputAttrs}
  //   //     options={options}
  //   //   />
  //   // )

  //   // if (formItemAttrs != null) {
  //   //   return <OmFormItem {...formItemAttrs}>{selectElem}</OmFormItem>
  //   // }

  //   // return selectElem;
  // }

  onInputChanged = () => {
    // console.log(' - onInputChanged - ')
    // this.forceUpdate()
    // this.bodyValue = this.editor.getContent();
  }

  openBccField = () => {
    this.setState({
      bccVisible: true,
    })
  }

  setDraftSavedTime = (time: Date | string | number) => {
    const timeStr = timeFormat(time, 'HH:mm')

    this.draftSavedAt = timeStr

    let str = ''
    if (this.draftSavedAt != null) {
      str = i18n.t('email.desc.draftSavedAt', { time: this.draftSavedAt })
    }

    // const draftSavedTimeElem = <span className={classNames('draft-time')}>{str}</span>

    this.draftSavedTimeStr = str

    if (this.setDraftSavedTimeStr != null) {
      this.setDraftSavedTimeStr(str)
    }

    // const formInfo = this.createFormInfo()

    // this.setState({
    //   formInfo,
    // })
  }

  /**
   * PR-7a-3: `<div ref={setEditorContainer}>` 의 ref 콜백.
   *
   * 컨테이너 div 가 마운트되면 어댑터 `lifecycle.init({ host: 'iframe' })` 을 호출하여
   * `IframeHost` 가 div 안에 iframe 을 동적으로 생성하도록 한다. 이전 (PR-7a-2 까지) 의
   * `setIFrame` + `startInit` + `initEditorApi` 경로는 어댑터 lifecycle 내부 (IframeHost
   * loadHtml 폴링 + TinyMCELifecycle 의 `parent.initEditorNative` 트램폴린 + NineNative
   * forward) 로 흡수되었다.
   *
   * - 마운트 후 init 은 한 번만 트리거 (`editorInitStarted` 가드).
   * - init 완료 후 `this.iframe = host.getIframeElement()` 로 기존 코드 (60+ `this.iframe`
   *   접근부) 와의 호환성 유지. PR-7b/PR-7c 에서 점진 제거.
   * - 동일 div 의 unmount 시점에는 `componentWillUnmount` 의 `lifecycle.dispose()` 가
   *   teardown 수행.
   */
  setEditorContainer = (ref: HTMLDivElement | null): void => {
    if (ref == null) {
      return
    }
    if (this.editorInitStarted === true) {
      return
    }
    this.editorInitStarted = true
    this.editorContainer = ref
    // draft-body-style-fidelity: draft/resend 재열람은 원본 body style 을 첫 마운트 前에 시딩하기
    // 위해 reqDetail 을 await 해야 하므로 async initEditor 로 위임한다. ref 콜백은 반환값을 무시한다.
    void this.initEditor(ref)
  }

  /**
   * 편집기 lifecycle.init 트리거.
   *
   * NgwEditor + draft/resend 재열람이면 원본 `<body style>` 을 파싱해 defaultStyle 로 시딩(B′
   * pre-mount)하여 재열람 시 본문 글자색이 사용자 설정(#000000)으로 소실되는 회귀를 방지한다.
   * body 를 로드 후 setDefaultStyle 로 바꾸는 대신 첫 마운트 前에 확정하므로 편집기 remount 가
   * 발생하지 않는다(무단 auto-save/flicker/undo 손실 회피). (draft-body-style-fidelity)
   */
  private initEditor = async (ref: HTMLDivElement): Promise<void> => {
    const { myWin, onSetIFrame, helper } = this.props
    const hash = conf.gitInfo.commit.hash
    const htmlUrl = 'editor/tinyMCE_desktop.html?hash=' + hash

    // PR-N10 (0.2.11): NgwEditor 어댑터는 init 시점에 nineMailId 가 필요. helper 의 nineMailId 를
    // 사용하되 helper 가 undefined 인 라우트 (/edit, dead) 는 ncompose-{uuid} fallback.
    // 첫 글자 `n` 은 legacy nine_tinymce_desktop 호환을 위해 강제.
    const nineMailId: string =
      typeof helper?.getNineMailId === 'function'
        ? helper.getNineMailId()
        : `ncompose-${safeUUID()}`

    // C7: NgwEditor 한정으로 첫 마운트부터 사용자 기본 폰트 적용. TinyMCE 어댑터는 본 인자를
    // 무시하므로 ngw 분기에서만 computeDefaultStyle 호출 (불필요 계산 회피).
    let defaultStyle = editorType === 'ngw' ? this.computeDefaultStyle() : undefined

    // draft-body-style-fidelity: draft/resend 재열람이면 원본 body style 로 defaults 시딩.
    // reqDetail 은 캐시되므로 onReady 경로의 재호출은 캐시 히트. 실패/미검출 시 사용자 설정 유지.
    if (editorType === 'ngw' && defaultStyle != null) {
      const reopenSource = this.getReopenBodyStyleSource()
      if (reopenSource != null) {
        const bodyHTML = await this.loadReopenBodyHTML(reopenSource)
        // await 도중 unmount 되었으면 detached 상태 init 방지(editor root/host 누수 차단).
        if (this.isUnmounted === true || this.editorContainer !== ref) {
          return
        }
        if (bodyHTML != null && bodyHTML.length > 0) {
          // draft-body-style-from-viewer: 뷰어가 draft 표시 파싱에서 보고해 둔 body 스타일 골격이
          // 있으면(길이·prefix 검증 통과) 그 골격(수백 B)만 파싱해 전체 파싱(~4s)을 생략한다.
          // 골격 추출·검증 설계는 draftBodyStyleSkeleton.ts / draftBodyStyleCache.ts 참조.
          // 이 경로에선 reopenParsedDoc 이 없으므로 본문 적용(convertCid)이 스스로 파싱한다
          // (총 파싱 횟수는 동일하게 1회 — 위치만 에디터 init 뒤로 이동해 init 이 즉시 열린다).
          const draftId = helper?.getDraftEmail?.()?.getId?.() ?? null
          // parsed-body-doc-from-viewer: 뷰어가 표시 파싱 직후(mutate 전) 떠 둔 원본 보존
          // Document 가 있으면(원문 완전 일치 — takeParsedBodyDocument 가 검증) 그것을 그대로
          // reopenParsedDoc 레일에 태운다: 시딩(parseBodyDefaultStyle, 읽기 전용)과 본문 적용
          // (convertCid→setDataFromDOM/getSafeElement)이 재파싱 없이 공유 — 편집(draft)/
          // 다시 보내기(resend) 열기의 전체 파싱(~4s) 제거. key 는 reopen 소스와 동일한
          // 이메일(draft 우선, 없으면 resend — getReopenBodyStyleSource 와 같은 우선순위).
          const resendId =
            helper?.getExtValues?.()?.resendEmail?.getId?.() ??
            helper?.getExtValues?.()?.resendEmail?.data?.id ??
            null
          const reopenEmailId = draftId ?? resendId
          const viewerDoc =
            BODY_DOC_FROM_VIEWER && reopenEmailId != null
              ? takeParsedBodyDocument(reopenEmailId, bodyHTML)
              : null
          const skeleton =
            viewerDoc == null && EmailForm.BODY_STYLE_FROM_VIEWER && draftId != null
              ? takeDraftBodyStyleSkeleton(draftId, bodyHTML)
              : null
          let parsed: ReturnType<typeof parseBodyDefaultStyle>
          if (viewerDoc != null) {
            parsed = parseBodyDefaultStyle(viewerDoc)
            this.reopenParsedDoc = viewerDoc
            this.reopenParsedHTML = bodyHTML
          } else if (skeleton != null) {
            parsed = parseBodyDefaultStyle(skeleton)
          } else {
            // draft-reopen-parse-share: 여기서 1회만 파싱해 style seeding 과 본문 적용(convertCid)
            // 이 공유한다. parseBodyDefaultStyle 는 읽기 전용이라 이후 cid 변환이 안전하게 mutate 가능.
            const reopenDoc = new DOMParser().parseFromString(bodyHTML, 'text/html')
            parsed = parseBodyDefaultStyle(reopenDoc)
            this.reopenParsedDoc = reopenDoc
            this.reopenParsedHTML = bodyHTML
          }
          const seeded = mergeDraftDefaults(parsed, defaultStyle)
          defaultStyle = seeded
          this.reopenSeededStyle = seeded
          // body 에 명시 color 가 있을 때만 본문 텍스트를 그 색의 mark 로 승격(toolbar 반영용).
          this.reopenBodyColor =
            parsed.color != null && parsed.color.trim().length > 0 ? parsed.color : null
        }
      }
    }

    // NgwEditor 는 locale 이 마운트 시점 초기값이므로, init 전에 현재 앱 언어를 setLocale 로
    // 저장해 첫 마운트부터 올바른 언어로 렌더한다 (root == null 이라 렌더 없이 currentLocale 만
    // 저장; onReady 의 applyLanguage 는 NoOp 로 흡수). TinyMCE 는 onReady 경로에서 처리.
    if (editorType === 'ngw') {
      this.applyLanguage()
    }
    try {
      await this.editorAdapter.lifecycle.init(
        buildEditorInitOptions(editorType, ref, htmlUrl, nineMailId, defaultStyle),
      )
      // 'ngw' 모드는 inline 마운트라 iframe 이 존재하지 않는다. 본 가드는 'tinymce' 모드의
      // iframe DOM 직접 접근 (visibility 토글) 만을 위해 남는다. 어댑터 호출은 모두
      // lifecycle.isReady() 가드로 분리되어 있다 (PR-N7 hotfix, 2026-05-13).
      if (editorType !== 'ngw') {
        const host = this.editorAdapter.host as IframeHost
        const iframe = host.getIframeElement()
        this.iframe = iframe
        if (onSetIFrame != null) {
          onSetIFrame(iframe)
        }
      }
      // myWin 은 PR-7a-2 까지의 editorExt.ts thin delegate 가 trampoline 설치 시 사용했으나
      // 어댑터 lifecycle 은 container.ownerDocument.defaultView 를 직접 사용하므로 더 이상
      // 필요 없다. 단, EmailForm 의 다른 호출부 (`componentWillUnmount` 등) 가 myWin 을
      // 계속 사용하므로 prop 자체는 유지.
      void myWin
    } catch (err: unknown) {
      console.error('EmailForm: editor lifecycle.init failed', err)
    }
  }

  /**
   * draft-body-style-fidelity: 재열람(draft/resend) body style 시딩 소스를 반환한다.
   * reply/forward(prevEmail)·신규는 null (기존 computeDefaultStyle 사용자 설정 유지).
   */
  private getReopenBodyStyleSource = (): ReopenBodySource | null => {
    const { helper } = this.props
    const draftEmail = helper?.getDraftEmail?.() ?? null
    if (draftEmail != null) {
      return draftEmail as unknown as ReopenBodySource
    }
    const extValues = helper?.getExtValues?.()
    const resendEmail = extValues?.resendEmail ?? null
    return resendEmail != null ? (resendEmail as ReopenBodySource) : null
  }

  /**
   * draft-body-style-fidelity: 재열람 소스에서 원본 HTML 본문을 가져온다.
   * reqDetail 실패 또는 텍스트 본문(HTML 아님)이면 null (호출부 사용자 설정 fallback).
   */
  private loadReopenBodyHTML = async (source: ReopenBodySource): Promise<string | null> => {
    try {
      const detail = await source.reqDetail()
      const bodyPart = detail?.bodyPart
      if (bodyPart != null && bodyPart.hasHTMLBody() === true) {
        return bodyPart.getHTMLBody()
      }
      return null
    } catch (err) {
      console.warn('EmailForm: reopen body style 파싱용 reqDetail 실패, 사용자 설정 fallback', err)
      return null
    }
  }

  getEditElem = () => {
    // 에디터 컨테이너 div 는 캐시한다 — ref 로 ngwEditor 가 마운트되므로 재생성되면 remount(본문 소실)된다.
    if (this.editElem == null) {
      this.editElem = (
        <div
          className={classNames(styles.htmlEditor, 'htmlEditor')}
          ref={(ref) => {
            this.setEditorContainer(ref)
          }}
        />
      )
    }

    // editor-image-tools — 이미지 선택 시 회전/반전/크기 Floating 툴바(ngwEditor 전용).
    // ImageToolbar 는 캐시하지 않고 매 렌더 재구성한다 — labels 를 1회 고정하면 이메일 편집 중
    // 런타임 언어 변경(onLanguageChanged→forceUpdate)이 반영되지 않는다(OMD-228). body 로 portal
    // 되므로 트리 위치는 무관하고, 에디터 div 는 위에서 캐시돼 remount 되지 않는다.
    return (
      <>
        {this.editElem}
        <ImageToolbar
          editorImage={this.editorAdapter.image}
          enabled={editorType === 'ngw'}
          labels={buildImageToolbarLabels((key) => i18n.t(key))}
        />
      </>
    )
  }

  // tagRender = (props) => {
  //   const { label, value, disabled, closable, onClose } = props

  //   let className = classNames('email-tag')

  //   if (isEmail(value) === true) {
  //     if (value != null && value.endsWith(this.domain) === true) {
  //       className = classNames('email-tag', styles.innerEmail)
  //     } else {
  //       className = classNames('email-tag', styles.outerEmail)
  //     }
  //   } else {
  //     className = classNames('email-tag', styles.notEmail)
  //   }

  //   const tagAttrs = {
  //     className,
  //     disabled,
  //     closable,
  //     onClose,
  //     // style: { marginInlineEnd: 4 },
  //   }
  //   return <Tag {...tagAttrs}>{label}</Tag>
  // }

  onValuesChanged = (values?: any) => {
    console.log(' - onValuesChanged - ', values)

    if (this.editorAdapter?.lifecycle.isReady() === true) {
      // PR-7b: editorMgr.checkNoInput → state.checkNoInput.
      this.editorAdapter.state.checkNoInput()
    }
    if (values != null && values.subject != null) {
      if (this.prevFormValues != null && this.prevFormValues.subject != values.subject) {
        this.notifyTitleChanged(values.subject)
      }
    }

    // prevFormValues 업데이트 - 값이 변경되었는지 확인을 위해 필요.
    if (values != null) {
      Object.keys(values).forEach((key) => {
        this.prevFormValues[key] = values[key]
      })
    }
  }

  notifyTitleChanged = (title: string) => {
    const { helper } = this.props
    if (helper != null && helper.notifyTitleChanged != null) {
      if (this.titleChangeTimer != null) {
        clearTimeout(this.titleChangeTimer)
      }
      this.titleChangeTimer = setTimeout(() => {
        helper.notifyTitleChanged(title)
        this.titleChangeTimer = null
      }, 200)
    }
  }

  createFormInfo = () => {
    // const { sendAsAccounts } = this.props
    const self = this

    // const sendAsAccountList = sendAsAccounts || []

    const initialValues = this.getInitialValues()

    const info = {
      labelKey: 'email.field',
      formAttrs: {
        className: classNames(styles.emailForm),
        ref: this.setFormRef,
        layout: 'horizontal',
        // labelCol: { span: 4 },
        // wrapperCol: { span: 18 },
        initialValues,
        onValuesChange: (values) => {
          this.onValuesChanged(values)
        },
        onFinish: this.onFinish,
        onFinishFailed: this.onFinishFailed,
      },
      items: {
        from: {
          attrs: {
            rules: [{ required: true }],
          },
          inputAttrs: {
            // mode: 'multiple',
            suffixIcon: <img src={chevronDown24} alt="chevron-down" />,
            // suffixIcon: null,
            // disabled: sendAsAccountList.length === 0,
          },
          getInputElem: (state, attrs, inputAttrs) => {
            return this.createFromSelect(
              inputAttrs,
              state.toInfo,
              (options) => {
                return { fromInfo: { options } }
              },
              attrs,
            )
          },
        },
        to: {
          attrs: {
            name: null,
          },
          // inputAttrs: {
          //   mode: 'multiple',
          //   // tokenSeparators: [',', ';', ' '],
          //   suffixIcon: null,
          //   tagRender: this.tagRender,
          // },
          getInputElem: (state, attrs /*, inputAttrs */) => {
            // const formItemAttrs = {
            //   name: 'to',
            //   // getValueProps: (value) => {
            //   //   console.log(' - getValueProps : ', value)
            //   //   return value
            //   // },
            //   rules: [{ required: false }],
            // }

            // const selectElem = this.createEmailSelect(
            //   'to',
            //   inputAttrs,
            //   state.toInfo,
            //   (options) => {
            //     return { toInfo: { options } }
            //   },
            //   formItemAttrs,
            // )

            const selectElem = (
              <SharedEmailSelect
                innerRef={this.toRef}
                innerDomain={this.domain}
                initialFocus={false}
                dataTestId="mail-compose-to"
              />
            )

            return (
              <OmFormItem key={attrs.name} {...attrs}>
                <OmRow className={classNames(styles.fromField)}>
                  <OmCol>{selectElem}</OmCol>
                  <OmCol>
                    <OmButton
                      className={classNames(styles.bccBtn)}
                      variant="text"
                      onClick={this.openBccField}
                    >
                      {i18n.t('email.field.bcc')}
                    </OmButton>
                  </OmCol>
                </OmRow>
              </OmFormItem>
            )
          },
        },
        cc: {
          attrs: {
            rules: [{ required: false }],
          },
          // input: Select,
          // inputAttrs: {
          //   mode: 'multiple',
          //   // tokenSeparators: [',', ';', ' '],
          //   suffixIcon: null,
          //   tagRender: this.tagRender,
          // },
          getInputElem: (state, attrs /* , inputAttrs */) => {
            // return this.createEmailSelect(
            //   'cc',
            //   inputAttrs,
            //   state.ccInfo,
            //   (options) => {
            //     return { ccInfo: { options } }
            //   },
            //   attrs,
            // )

            const selectElem = (
              <SharedEmailSelect
                innerRef={this.ccRef}
                innerDomain={this.domain}
                initialFocus={false}
                dataTestId="mail-compose-cc"
              />
            )

            return (
              <OmFormItem key={attrs.name} {...attrs}>
                {selectElem}
              </OmFormItem>
            )
          },
        },
        bcc: {
          attrs: {
            rules: [{ required: false }],
          },
          // input: Select,
          // inputAttrs: {
          //   mode: 'multiple',
          //   tokenSeparators: [',', ';', ' '],
          //   suffixIcon: null,
          //   tagRender: this.tagRender,
          // },
          getInputElem: (state, attrs /*, inputAttrs */) => {
            // return this.createEmailSelect(
            //   'bcc',
            //   inputAttrs,
            //   state.bccInfo,
            //   (options) => {
            //     return { bccInfo: { options } }
            //   },
            //   attrs,
            // )
            const selectElem = (
              <SharedEmailSelect
                innerRef={this.bccRef}
                innerDomain={this.domain}
                initialFocus={false}
                dataTestId="mail-compose-bcc"
              />
            )

            return (
              <OmFormItem key={attrs.name} {...attrs}>
                {selectElem}
              </OmFormItem>
            )
          },
        },
        subject: {
          attrs: {
            className: classNames('subject-item'),
            rules: [{ required: false }],
          },
          inputAttrs: {
            className: classNames(styles.subjectInput),
            placeholder: i18n.t('email.placeholder.subject'),
            'data-testid': 'mail-subject-input',
            // suffix: draftSavedTimeElem,
          },
          getInputElem: (state, attrs, inputAttrs) => {
            // SubjectInputEx 는 모듈 스코프에서 정의됨 — render 중 재선언 금지 (회귀 방지:
            // 컴포넌트 type 이 매 render 마다 달라지면 input 이 unmount/remount 되어 자동
            // 저장 시점에 포커스가 사라진다).
            return (
              <SubjectInputEx
                key={attrs.name}
                attrs={attrs}
                inputAttrs={inputAttrs}
                initialSuffix={this.draftSavedTimeStr}
                registerSetSuffix={(setter) => {
                  self.setDraftSavedTimeStr = setter
                }}
                onTabToEditor={() => {
                  // subject-tab-to-editor: 제목 입력 후 Tab → editor 본문으로 focus 이동
                  // + cursor 위치 자동 설정 (빈 본문 → nBody 맨 앞, 채워진 본문 → nBody 끝).
                  // editor lifecycle 미준비 시 silent return → 호출자 측 preventDefault 가 이미
                  // 호출되었어도 callback 자체는 아무 것도 안 함 (사용자 입장에서 focus 유지).
                  const editor = self.editorAdapter
                  if (editor == null || editor.lifecycle.isReady() !== true) return
                  const isEmpty = editor.content.isEmpty()
                  editor.cursor.setIntoNBody(isEmpty)
                  editor.cursor.focus()
                }}
              />
            )
          },
        },
      },
    }

    return info
  }

  setFormValues = (formValues: any) => {
    const values = {
      ...formValues,
    }

    if (values.from != null) {
      let from = values.from
      if (Array.isArray(values.from)) {
        from = values.from[0]
      }
      if (from != null && from.email != null) {
        values.from = from.email
      }
      const existingAccount = this.sendAccountList.find((account) => account.email === values.from)
      if (existingAccount == null) {
        values.from = this.sendAccountList[0].email
      }
    }

    this.prevFormValues = {
      ...values,
    }

    if (this.formRef != null) {
      console.log(' - setFormValues : ', values)
      this.formRef.setFieldsValue(values)

      // if (this.setFromInfo != null && values.from != null) {
      //   this.setFromInfo(this.userInfo)
      // }
      if (values.to != null && values.to.length > 0) {
        if (typeof values.to[0] === 'string') {
          this.toRef.current.setEmailList(values.to)
        } else {
          this.toRef.current.setUserInfoList(values.to)
        }
      }
      if (values.cc != null && values.cc.length > 0) {
        if (typeof values.cc[0] === 'string') {
          this.ccRef.current.setEmailList(values.cc)
        } else {
          this.ccRef.current.setUserInfoList(values.cc)
        }
      }
      if (values.bcc != null && values.bcc.length > 0) {
        if (typeof values.bcc[0] === 'string') {
          this.bccRef.current.setEmailList(values.bcc)
        } else {
          this.bccRef.current.setUserInfoList(values.bcc)
        }
        this.openBccField()
      }
    }
  }

  checkSecureSendHeader = (headers: any) => {
    if (headers != null) {
      const expirationKeyItem = headers.find(
        (h: any) => h.name.toLowerCase() === 'x-secure-expirationkey',
      )

      if (expirationKeyItem != null && expirationKeyItem.value != null) {
        const VALID_KEYS: ExpirationKey[] = ['1day', '1week', '1month', '6months', '1year']
        const rawKey = expirationKeyItem.value.trim()
        if (!VALID_KEYS.includes(rawKey as ExpirationKey)) return

        const expirationKey = rawKey as ExpirationKey
        const expired = calculateExpiredISO(expirationKey)
        this.setState({
          secureSendEnabled: true,
          secureSendExpired: expired,
          secureSendExpirationKey: expirationKey,
          securityWarningVisible: true,
        })
      }
    }
  }

  checkReadReceiptHeader = (headers: any) => {
    if (headers != null) {
      const item = headers.find((h: any) => h.name.toLowerCase() === 'x-readreceipt-enabled')
      if (item != null && item.value != null) {
        const enabled = item.value.trim() === 'true'
        if (enabled) {
          this.setState({ readReceiptEnabled: true })
        }
      }
    }
  }

  toggleReadReceipt = () => {
    this.setState((prev) => ({
      readReceiptEnabled: !prev.readReceiptEnabled,
    }))
  }

  setVisible = (visible: boolean) => {
    this.setState({ invisible: visible === false })
  }

  initFocus = () => {
    setTimeout(() => {
      this.saveFormValues()
      this.setVisible(true)
      this.placeInitialFocus()
    }, 200)
  }

  // reply-editor-quoted-lazy-bake Phase 5: initFocus 에서 캐럿/포커스 라우팅만 분리.
  // reply 는 onLoaded 진입 즉시(서명·인용 도착 전) 호출해 캐럿을 nBody 에 선배치하고, 이후
  // 수렴점(서명∧인용 완료 후 initFocus — reply-quoted-load-before-edit)의 재호출은 focusInfo
  // 가드로 no-op. saveFormValues(isModified baseline)·setVisible 은 분리하지 않고 수렴점에
  // 남긴다 — baseline 을 서명 도착 전에 잡으면 서명의 inline attachment 추가로
  // isModified=true 가 되어 빈 reply 가 자동저장되는 회귀.
  placeInitialFocus = () => {
    if (this.focusInfo !== 0) return

    // 에디터 내부 selection 을 항상 nBody 로 1회 배치. 시각적 focus 가 To/subject 로 가는
    // 케이스에서도 에디터 캐럿을 서명 바로 위(빈 본문이면 맨 앞)에 고정한다 — 그렇지 않으면
    // 콘텐츠 삽입 당시 자동 설정된 위치가 그대로 남는다. focusInfo 가드로 1회만 실행된다.
    // setIntoNBody 는 selection 만 dispatch 하고 view focus 를 뺏지 않아 아래 To/subject focus
    // 라우팅과 충돌하지 않는다.
    if (this.editorAdapter?.lifecycle.isReady() === true) {
      this.editorAdapter.cursor.setIntoNBody(this.editorAdapter.content.isEmpty())
    }
    const values = this.getInitialValues()
    if (values.to == null || values.to.length === 0) {
      this.focusInfo = 1
      this.toRef.current.getRef().focus()
    } else if (values.subject == null || values.subject.length === 0) {
      this.focusInfo = 2
      this.formRef.getFieldInstance('subject').focus()
    } else if (this.editorAdapter?.lifecycle.isReady() === true) {
      this.focusInfo = 3
      // PR-7c: editorWindow.focus / setCursorIntoNBody0 → adapter host.focus / cursor.setIntoNBodyCore.
      // nBody selection 배치는 위 진입부에서 이미 수행 — 여기선 시각 focus 만 맞춘다.
      this.editorAdapter.host.focus()
      // NgwEditor inline 에서는 host.focus() 가 window.focus() 일 뿐이라 PM contenteditable 에
      // 실제 focus 가 가지 않는다. 직전 To/CC EmailSelect 가 잡은 focus 가 남는 회귀가 있었으므로
      // cursor.focus()(=view.focus()) 로 본문에 명시 focus.
      this.editorAdapter.cursor.focus()
      // draft-body-style-fidelity: 커서 배치 직후 캐럿에 기본 글자색 stored mark 설정.
      // 캐럿 배치보다 늦게 도착하는 서명/인용은 addToHistory:false 주입이라 storedMarks 를
      // 보존하므로(NgwEditorMailRegion.markBackgroundInjection) 이 seed 가 지워지지 않는다.
      this.seedCaretColorMark()
    }
  }

  getFormValues = (forSave?: boolean): any => {
    let values: any = {}
    if (this.formRef != null) {
      const formValues = this.formRef.getFieldsValue()
      values = { ...formValues }

      if (values.from != null && typeof values.from === 'string') {
        const sendAccount = this.sendAccountList.find((account) => account.email === values.from)
        if (sendAccount != null) {
          values.from = [
            {
              name: sendAccount.name,
              email: sendAccount.email,
            },
          ]
        }
      }
    }

    if (this.toRef.current != null) {
      values.to = this.toRef.current.getValidUserInfoList(forSave)
    }
    if (this.ccRef.current != null) {
      values.cc = this.ccRef.current.getValidUserInfoList(forSave)
    }
    if (this.bccRef.current != null) {
      values.bcc = this.bccRef.current.getValidUserInfoList(forSave)
    }

    const { secureSendEnabled, secureSendExpirationKey, readReceiptEnabled } = this.state
    if (forSave !== false && secureSendEnabled === true && secureSendExpirationKey != null) {
      values['header:X-Secure-ExpirationKey:asText'] = secureSendExpirationKey
    } else {
      values['header:X-Secure-ExpirationKey:asText'] = null
    }
    // 수신확인 모드를 임시저장에 보존 — 다시 열 때 checkReadReceiptHeader가 복원
    if (forSave !== false && readReceiptEnabled === true) {
      values['header:X-ReadReceipt-Enabled:asText'] = 'true'
    } else {
      values['header:X-ReadReceipt-Enabled:asText'] = null
    }
    return values
  }

  isEmpty = () => {
    if (this.formRef != null && this.editorAdapter.lifecycle.isReady() === true) {
      if (this.attachments != null && this.attachments.length > 0) {
        return false
      }

      const inlineAttachments = Object.values(this.inlineAttachmentMap).map((item) => item)

      if (inlineAttachments != null && inlineAttachments.length > 0) {
        return false
      }

      const values = this.getFormValues()

      const { to, cc, bcc, subject } = values
      if (to != null && to.length > 0) {
        return false
      }
      if (cc != null && cc.length > 0) {
        return false
      }
      if (bcc != null && bcc.length > 0) {
        return false
      }

      if (subject != null && subject.length > 0) {
        return false
      }

      // PR-7c: editorWindow.isEmpty → adapter.content.isEmpty.
      if (this.editorAdapter.content.isEmpty() !== true) {
        return false
      }
    }

    return true
  }

  getEmailValues = (forSave?: boolean): any => {
    if (this.formRef != null && this.editorAdapter.lifecycle.isReady() === true) {
      // const instance1 = this.formRef.getFieldInstance('from')
      // const instance2 = this.formRef.getFieldInstance('to')
      // console.log(' - instance - ', instance1, instance2)

      const values = this.getFormValues(forSave)

      // PR-7c: editorWindow.getData → adapter.content.getData.
      // PR-N9a-2: 송신/저장 경로 — urlMap 역치환 + onPreProcess 발화를 포함하는
      // getDataPreprocessed 사용. NgwEditor 모드에서 data URL → cid:/blob: 역치환과
      // inlineAttachmentMap 재구축이 본 메서드 내부에서 일어남.
      const body = this.editorAdapter.content.getDataPreprocessed()

      // const text = editorWindow.getData(false)

      const text = convert(body, {
        selectors: [{ selector: 'img', format: 'skip' }],
      })

      const inlineAttachments = Object.values(this.inlineAttachmentMap).map((item) => item)

      const ret = {
        ...values,
        // from 필드에 이름과 이메일을 넣기 위해 새로 정의 ==> settings에서 가져옴.
        // from: [this.userInfo],
        htmlBody: body,
        htmlText: text,
        attachments: [...this.attachments, ...inlineAttachments],
        sharingAttachments: [...this.sharingAttachments],
      }

      // console.log('=== getEmailValues DEBUG ===')
      // console.log('this.attachments:', this.attachments)
      // console.log('inlineAttachments:', inlineAttachments)
      // console.log('combined attachments:', ret.attachments)
      // ret.attachments.forEach((attachment, index) => {
      //   console.log(`Combined attachment ${index}:`)
      //   console.log('  - attachment:', attachment)
      //   console.log('  - attachment.file:', attachment.file)
      //   console.log('  - attachment.originFile:', attachment.originFile)
      //   console.log('  - attachment keys:', Object.keys(attachment))
      // })
      // console.log('=== END getEmailValues DEBUG ===')

      return ret
    }

    return null
  }

  saveFormValues = () => {
    if (this.formRef != null && this.editorAdapter.lifecycle.isReady() === true) {
      const values = this.getFormValues()

      this.saved = {
        formJson: JSON.stringify(values),
        attachments: [...this.attachments],
      }

      // PR-7b: resetChangedState → state.resetChangedState.
      this.editorAdapter.state.resetChangedState()
    }
  }

  isModified = (target?: any): boolean => {
    const saved = target || this.saved

    if (
      saved != null &&
      saved.formJson != null &&
      this.formRef != null &&
      this.editorAdapter.lifecycle.isReady() === true
    ) {
      const values = this.getFormValues()

      if (JSON.stringify(values) !== saved.formJson) {
        return true
      }

      if (
        this.attachments.length !== saved.attachments.length ||
        this.attachments.some((attachment, idx) => {
          return saved.attachments[idx].file.blobId !== attachment.file.blobId
        })
      ) {
        return true
      }

      // PR-7b: isNBodyChanged / isQuotedChanged → mail.region.<...>.isChanged().
      if (
        this.editorAdapter.mail.region.nBody.isChanged() ||
        this.editorAdapter.mail.region.quotedBody.isChanged()
      ) {
        return true
      }
    }
    return false
  }

  onFinish = () => {
    this.callSend()
  }

  onFinishFailed = (formObj: any) => {
    console.log('onFinishFailed - ', formObj)

    const { onFinishFailed } = this.props

    if (onFinishFailed != null) {
      onFinishFailed(formObj)
    }
  }

  cancel = (forced?: boolean) => {
    const { onCanceled } = this.props

    // draft가 저장상태이면, 저장된 내용과 현재의 내용이 다르면, 확인창 표시.
    // draft가 저장상태가 아니면, 최초의 저장 내용과 다르면 확인창 표시.
    const saved = this.saved
    if (forced !== true && this.isModified(saved) === true) {
      const modalObj = confirmBase({
        wrapClassName: classNames(styles.editorConfirm),
        icon: (
          <img src={exclamationCircleBlue22} style={{ paddingRight: '16px' }} alt="exclamation" />
        ),
        title: i18n.t('editor.title.saveDraft'),
        content: i18n.t('editor.confirm.saveDraft'),
        okText: i18n.t('common.button.save'),
        cancelText: i18n.t('common.button.cancel'),
        onOk: () => {
          this.callSave()
          if (onCanceled != null) {
            onCanceled()
          }
        },
        onCancel: () => {
          if (onCanceled != null) {
            onCanceled()
          }
        },
        footer: (_, { OkBtn, CancelBtn }) => (
          <div className={classNames(styles.footer)}>
            <div className={classNames('btns')}>
              <OmButton
                className={classNames('delete', 'h38', 'w100')}
                onClick={() => {
                  modalObj.destroy()
                  this.deleteDraft(true)
                }}
              >
                {i18n.t('common.button.discard')}
              </OmButton>
              <CancelBtn />
              <OkBtn />
            </div>
          </div>
        ),
      })
    } else {
      if (onCanceled != null) {
        onCanceled()
      }
    }
  }

  deleteDraft = (forced?: boolean) => {
    const { helper } = this.props

    if (forced === true) {
      helper.deleteDraft()
      this.cancel(true)
    } else {
      confirmBase({
        wrapClassName: classNames(styles.editorConfirm),
        icon: (
          <img src={exclamationCircleBlue22} style={{ paddingRight: '16px' }} alt="exclamation" />
        ),
        title: i18n.t('editor.title.discardDraft'),
        content: i18n.t('editor.confirm.discardDraft'),
        okText: i18n.t('common.button.discard'),
        cancelText: i18n.t('common.button.cancel'),
        onOk: () => {
          helper.deleteDraft()
          this.cancel(true)
        },
        onCancel: () => {},
      })
    }
  }

  // opts.addToHistory:false — 초기화 경로(reply/forward/신규) 전용. 입력 준비(undo 리셋)
  // 선실행 이후에 도착하는 서명 주입이 undo 스택에 기록되지 않게 한다. 사용자의 서명 변경
  // 조작(onToolbarClick 'signature')은 opts 없이 호출해 기존대로 undo 에 기록된다.
  setSignature = async (key, opts?: { addToHistory?: boolean }) => {
    if (this.editorAdapter?.lifecycle.isReady() === true) {
      let signatureList = dataManager.getSignatureList()
      // OMD-225: 목록이 비어 있고 아직 최초 로딩 전이면, 로딩을 기다린 뒤 재시도한다.
      // (로딩 미완료를 "서명 없음"으로 오인해 조용히 반환하던 문제 — 지정한 서명이 적용되지 않고 default로 보임)
      if (signatureList.length === 0 && dataManager.signatureAPI?.isFirstLoaded !== true) {
        try {
          const reloaded = await dataManager.signatureAPI?.reloadList?.()
          signatureList = Array.isArray(reloaded) ? reloaded : dataManager.getSignatureList()
        } catch (e) {
          console.warn('setSignature: 서명 목록 로딩 재시도 실패', e)
        }
      }
      if (signatureList.length === 0) {
        // 로딩을 마쳤는데도 서명이 하나도 없음(실제 none).
        console.warn('setSignature: 사용할 서명이 없습니다.', key)
        return
      }
      let signature = signatureList.find((item) => {
        return item.signatureId === key
      })
      if (signature == null) {
        // 로딩된 목록에 일치하는 서명이 없음.
        console.warn('setSignature: 일치하는 서명을 찾지 못했습니다.', key)
        return
      }
      let content = signature.content
      let attachments = []
      // default가 아닌 것은 mime 형식임.
      if (signature.signatureId !== 'default') {
        const parsed = await dataManager.signatureAPI.parseContent(content)
        if (parsed != null) {
          content = parsed.content
          attachments = parsed.attachments
        }
      }
      if (signature != null) {
        const { helper } = this.props
        const userInfo = helper.getUserInfo()
        const allData = (await orgChartHelper.loadData()) || { departments: [] }
        const myInfo = (await orgChartHelper.findContact(userInfo.email)) || {
          name: '',
          emails: [{ address: userInfo.email, primary: true }],
          organization_name: null,
          jobposition: null,
          mobiles: [],
        }
        console.log(' - myInfo - ', myInfo)

        function get(path, obj, fb = `$\{${path}}`) {
          return path.split('.').reduce((res, key) => {
            return res[key] != null ? res[key] : fb
          }, obj)
        }

        function parseTpl(template: string, map: any, fallback?: string) {
          return template.replace(/\$\{.+?}/g, (match) => {
            const path = match.substr(2, match.length - 3).trim()
            return get(path, map, fallback)
          })
        }

        const companyInfo = allData.departments.find((item) => {
          return item.parent_id == null
        })

        let emailInfo = myInfo.emails.find((item: any) => {
          return item.primary === true
        })
        if (emailInfo == null && myInfo.emails.length > 0) {
          emailInfo = myInfo.emails[0]
        }

        const departmentInfo =
          myInfo != null && myInfo.organization_name != null && myInfo.organization_name.length > 0
            ? myInfo.organization_name[0]
            : null

        attachments.forEach((file) => {
          const { contentId } = file
          // if (file.disposition !== 'attachment') {
          if (contentId != null) {
            const originFile = new File([file.content], file.fileName, {
              type: file.mimeType,
            })

            const attachmentObj = this.attachmentHelper.createAttachment(
              originFile,
              contentId,
              this,
              false, // 원본은 eml이지만, 이메일에 추가될 떄는 jmap 형식이 되어야 함.
            )
            this.addInlineAttachment(attachmentObj, contentId)

            setTimeout(() => {
              requestAnimationFrame(() => {
                attachmentObj.checkUpload()
              })
            }, 500)
          }
        })

        const htmlText = parseTpl(content, {
          display_name: myInfo.name || '',
          company: companyInfo != null ? companyInfo.name : '',
          job_title: myInfo.jobposition != null ? myInfo.jobposition.name : '',
          email: emailInfo != null ? emailInfo.address : '',
          department:
            departmentInfo != null && departmentInfo.length > 1
              ? departmentInfo.slice(1).join('/')
              : '',
          mobile: myInfo.mobiles.length > 0 ? myInfo.mobiles[0].value : '',
        })
        // PR-7b: setSignature → mail.region.signature.set.
        // PR-N9a-1: NgwEditor schema parseDOM 이 cid: 를 drop 하므로 signature 본문에 포함된
        // cid 이미지를 사전에 data URL 로 변환 + urlMap 등록. TinyMCE 모드는 기존 동작 유지.
        // reply-quoted-dom-direct-apply: quotedBody 와 동일하게 cid-first(Document)→getSafeElement
        // (keepRoot, DocumentFragment)→set(fragment)로 재파싱 왕복을 제거. convertCidNgwToDocument 가
        // registerUrlMapping 을 호출하므로 전송 시 cid 역치환(getDataPreprocessed)은 동일 보장.
        if (editorType === 'ngw') {
          const cidDoc = await this.convertCidNgwToDocument(htmlText)
          const safeFragment = await getSafeElement(cidDoc, {
            keepRoot: true,
            dirAutoMode: 'preserve',
          })
          this.editorAdapter.mail.region.signature.set(safeFragment, {
            addToHistory: opts?.addToHistory,
          })
        } else {
          this.editorAdapter.mail.region.signature.set(htmlText)
        }
      }
    }
  }

  onToolbarClick = (type: string, value?: any) => {
    switch (type) {
      case 'send':
        if (this.formRef != null) {
          this.formRef.submit()
        }
        break
      case 'delete':
        this.deleteDraft()
        break
      case 'attachment':
        if (value === 'local') {
          this.openAttachment()
        } else if (value === 'share') {
          this.openAttachment(true)
        } else if (value === 'directcloud') {
          notImplemented('attachement - directcloud')
        }
        break
      case 'signature':
        if (value === 'none') {
          // PR-7b: removeSignature → mail.region.signature.remove.
          if (this.editorAdapter?.lifecycle.isReady() === true) {
            this.editorAdapter.mail.region.signature.remove()
          }
        } else {
          this.setSignature(value)
        }
        console.log(' - signature - ', value)
        break
      case 'more':
        if (value === 'saveDraft') {
          this.callSave(true)
        } else if (value === 'saveLocal') {
          this.callSaveLocal()
        } else if (value === 'close') {
          this.cancel()
        }
        break
      case 'scheduleSend':
        this.callScheduleSend()
        break
      case 'proposeFreeTime':
        this.callSelectAvailability()
        break
      case 'secureSend':
        this.openSecureSendPopup()
        break
      case 'readReceipt':
        this.toggleReadReceipt()
        break
      default:
        notImplemented(type)
    }
  }

  openSecureSendPopup = () => {
    secureSendOptions({
      mode: this.state.secureSendEnabled ? 'confidential' : 'normal',
      expirationKey: this.state.secureSendExpirationKey,
      onSave: (mode, expired, expirationKey) => {
        const enabled = mode === 'confidential'
        this.setState(
          {
            secureSendEnabled: enabled,
            secureSendExpired: expired,
            secureSendExpirationKey: expirationKey as ExpirationKey | null,
            securityWarningVisible: enabled,
          },
          () => this.callSave(true),
        )
      },
      onTrash: () => {
        this.setState(
          {
            secureSendEnabled: false,
            secureSendExpired: null,
            secureSendExpirationKey: null,
            securityWarningVisible: false,
          },
          () => this.callSave(true),
        )
      },
    })
  }

  onSecureSendDiscard = () => {
    confirmBase({
      wrapClassName: classNames(styles.secureSendDiscardConfirm),
      icon: <img src={exclamationCircleRed22} style={{ paddingRight: '16px' }} alt="exclamation" />,
      title: i18n.t('secureSend.discardTitle'),
      content: i18n.t('secureSend.discardDesc'),
      okText: i18n.t('common.button.yes'),
      cancelText: i18n.t('common.button.no'),
      onOk: () => {
        this.setState(
          {
            secureSendEnabled: false,
            secureSendExpired: null,
            secureSendExpirationKey: null,
            securityWarningVisible: false,
          },
          () => this.callSave(true),
        )
      },
      width: 480,
    })
  }

  notifyUploadProgress = (attachmentId: string, progress: number, error?: any) => {
    console.log('--- notifyUploadProgress : ', attachmentId, progress)
    const progressCB = this.attachmentUploadProgressMap[attachmentId]
    if (progressCB != null) {
      progressCB(null, progress, error)
    }
    this.updateSendBtnByUploadStatus()
  }

  callScheduleSend = () => {
    // 예약 메일은 이미 확인 팝업이 있으므로 confirmBeforeSend는 생략한다.
    // const { confirmBeforeSend } = this.props

    // 이메일 데이터 유효성 검사. 본문(htmlBody)·인라인 첨부는 검증에 불필요하므로 getEmailValues
    // (getDataPreprocessed→onPreProcess 로 inlineAttachmentMap 재구축하는 부수효과 동반)를 호출하지
    // 않고, 폼 값 + 사용자 첨부만으로 검증한다. 실제 본문/인라인 첨부는 이후 sendScheduled →
    // helper.save 의 getEmailValues 에서 재구축된다(중복 처리 제거).
    const formValues = this.getFormValues()
    const emailData = {
      to: formValues.to,
      cc: formValues.cc,
      bcc: formValues.bcc,
      subject: formValues.subject,
      attachments: this.attachments,
      sharingAttachments: this.sharingAttachments,
    }

    let invalidEmails = []
    const checkInvalidEmails = (emailInfos) => {
      if (emailInfos != null && emailInfos.length > 0) {
        const list = emailInfos.filter((item) => {
          const email = item.email || item
          if (email != null && typeof email === 'string' && email.trim().length > 0) {
            return isEmail(email) === false
          }
          return false
        })
        return list.map((item) => item.email || item)
      }
      return []
    }

    invalidEmails = invalidEmails.concat(checkInvalidEmails(emailData.to))
    invalidEmails = invalidEmails.concat(checkInvalidEmails(emailData.cc))
    invalidEmails = invalidEmails.concat(checkInvalidEmails(emailData.bcc))

    const warning = (options) => {
      confirmBase({
        wrapClassName: classNames(styles.editorConfirm),
        icon: (
          <img src={exclamationCircleRed22} style={{ paddingRight: '16px' }} alt="exclamation" />
        ),
        title: options.title,
        content: options.content,
        okText: i18n.t('common.button.ok'),
        onOk: () => {
          // 유효성 검사 실패 시 아무 작업 하지 않음
        },
        width: 380,
        footer: (_, { OkBtn /* , CancelBtn */ }) => (
          <div className={classNames(styles.footer)}>
            <div className={classNames('btns')}>
              <OkBtn />
            </div>
          </div>
        ),
      })
    }

    if (invalidEmails.length > 0) {
      const emails = invalidEmails.join(', ')
      warning({
        title: i18n.t('editor.title.invalidEmail'),
        content: i18n.t('editor.error.invalidEmail', { emails }),
      })
      return
    }

    // 받는 사람이 없는 경우 확인
    if (
      (!emailData.to || emailData.to.length === 0) &&
      (!emailData.cc || emailData.cc.length === 0) &&
      (!emailData.bcc || emailData.bcc.length === 0)
    ) {
      warning({
        title: i18n.t('editor.title.recipientError'),
        content: i18n.t('editor.error.recipientError'),
      })
      return
    }

    const isUploading = [
      ...(emailData.attachments || []),
      ...(emailData.sharingAttachments || []),
    ].some((attachment) => {
      const { isUploadProgressing, isUploading } = attachment
      return isUploadProgressing === true || isUploading === true
    })

    if (isUploading === true) {
      warning({
        title: i18n.t('editor.title.attachmentUploading'),
        content: i18n.t('editor.error.attachmentUploading'),
      })
      return
    }

    const sendScheduled = async (scheduledDateTime) => {
      const { helper, onSended } = this.props

      // popup opener 종료 시 예약 발송 불가 → 중단하고 안내(내용 보존).
      if (this.isPopupOpenerLost()) {
        this.notifyOpenerLost()
        return
      }

      if (helper != null) {
        this.setSendBtnDisabled(true)

        // console.log(' - callSend - start ');

        if (this.editorAdapter?.lifecycle.isReady() === true) {
          // PR-7b: send 실행 시 noInputTimer clear → state.clearNoInputTimer.
          this.editorAdapter.state.clearNoInputTimer()
        }

        // isSending 은 await 이전에 동기 설정.
        this.setState({
          isSending: true,
        })

        // editor-image-tools — 예약 발송 직전 회전 이미지 픽셀화(비파괴 캐시).
        await this.editorAdapter.image.prepareForSend()

        // reply-editor-quoted-lazy-bake: helper.save 가 동기 getEmailValues 를 소비하므로
        // 인용 bake 완료를 먼저 기다린다(미완료 시 예약 발송 본문에 인용 누락).
        await this.ensureQuotedBaked()

        // const draftEmail = helper.getDraftEmail();
        // if (draftEmail == null) {
        helper.save(true, false)
        // }

        const { secureSendEnabled, secureSendExpirationKey, readReceiptEnabled } = this.state
        // 만료일은 예약 시각 기준으로 계산한다. (현재 시각 기준이면 발송 전에 만료될 위험)
        const secureExpiresAt =
          secureSendEnabled && secureSendExpirationKey != null
            ? calculateExpiredISO(secureSendExpirationKey, scheduledDateTime)
            : null

        helper
          .sendScheduled(scheduledDateTime, {
            secureSendEnabled,
            secureExpiresAt,
            readReceiptEnabled,
          })
          .then((res) => {
            if (onSended != null) {
              onSended(res)
            }
            this.updateSendBtnByUploadStatus()
            this.deleteDraft(true)

            omMessage.success('Email scheduled successfully')
          })
          .catch((err) => {
            const message =
              err.name === 'AbortError' ? i18n.t('email.result.sendTimeout') : err.message
            errorBase({
              title: 'Error',
              content: message,
            })
            this.updateSendBtnByUploadStatus()
          })
          .finally(() => {
            this.setState({
              isSending: false,
            })
            // editor-image-tools — 예약 발송 흐름 종료 시 픽셀화 캐시 무효화.
            this.editorAdapter.image.clearPixelizeCache()
          })
      }
    }

    const send0 = () => {
      // 새로운 scheduleSend 함수 사용
      scheduleSend({
        title: i18n.t('mailbox.subtitle.scheduledSend'),
        onOk: (scheduledDateTime, closeFunc) => {
          sendScheduled(scheduledDateTime)
          closeFunc()
        },
        onCancel: () => {
          // 취소 시 특별한 동작 필요 없음
        },
        // onPreviewEML: () => {
        //   this.handleEMLPreview()
        // },
      })
    }

    // const confirmSend = () => {
    //   if (confirmBeforeSend === true) {
    //     confirmBase({
    //       title: i18n.t('email.title.confirmSendMessage'),
    //       content: i18n.t('email.desc.confirmSendMessage'),
    //       icon: <img src={exclamationCircleBlue22} alt="exclamation" />,
    //       okText: i18n.t('common.button.yes'),
    //       cancelText: i18n.t('common.button.no'),
    //       width: 480,
    //       onOk: () => {
    //         send0()
    //       },
    //     })
    //   } else {
    //     send0()
    //   }
    // }

    if (emailData.subject == null || emailData.subject.trim().length === 0) {
      confirmBase({
        wrapClassName: classNames(styles.editorConfirm),
        icon: (
          <img src={exclamationCircleRed22} style={{ paddingRight: '16px' }} alt="exclamation" />
        ),
        title: 'Warning',
        content: (
          <div>
            {i18n.t('editor.title.noSubject')}
            <br />
            {i18n.t('editor.warning.noSubject')}
          </div>
        ),
        okText: i18n.t('editor.button.noSubjectSend'),
        cancelText: i18n.t('common.button.cancel'),
        onOk: () => {
          send0() // confirmSend();
        },
        width: 480,
      })
    } else {
      send0() // confirmSend();
    }
  }

  callSelectAvailability = () => {
    console.log(' - callSelectAvailability - ')
    this.setState({
      isSelectAvailabilityOpen: true,
      schedulingNavDate: new Date(), // Reset to today when opening
    })
  }

  handleEMLPreview = async () => {
    try {
      // editor-image-tools — 미리보기도 수신자 뷰(픽셀 회전)를 반영하도록 픽셀화 캐시 준비.
      // PM 은 무손상이므로 미리보기 후 편집 복귀해도 회전 상태 유지.
      await this.editorAdapter.image.prepareForSend()
      // reply-editor-quoted-lazy-bake: 인용 bake 완료 후 수집해야 EML 에 인용이 포함된다.
      await this.ensureQuotedBaked()
      // 1. 이메일 데이터 수집
      const emailData = this.getEmailValues()

      if (!emailData) {
        omMessage.error('Failed to get email data')
        return
      }

      // 2. EmailObj를 사용하여 EML 생성
      const emlContent = await generateEMLUsingEmailObj(emailData)

      // 3. EML 유효성 검증
      const validationResult = validateEMLContent(emlContent, emailData)

      // 4. 개발 모드에서 콘솔에 출력
      if (import.meta.env.DEV) {
        console.log('Generated EML Content (via EmailObj):', emlContent)
        console.log('Email Data:', emailData)
        console.log('Validation Result:', validationResult)
      }

      // 5. EML 프리뷰 모달 표시
      emlPreview({
        title: 'EML Preview - Schedule Send',
        emlContent,
        emailData,
        validationResult,
        onOk: (closeFunc) => {
          closeFunc()
        },
      })
    } catch (error) {
      console.error('EML Preview failed:', error)
      omMessage.error('Failed to generate EML preview')
    } finally {
      // editor-image-tools — 미리보기 종료 시 픽셀화 캐시 무효화(이후 draft autosave strip 방지).
      this.editorAdapter.image.clearPixelizeCache()
    }
  }

  getTotalAttachmentSize = (): number => {
    let totalSize = 0
    this.attachments
      .filter((attachment) => {
        return attachment.sendType === 'jmap'
      })
      .forEach((attachment) => {
        totalSize += attachment.file.size
      })
    Object.values(this.inlineAttachmentMap).forEach((attachment) => {
      totalSize += attachment.file.size
    })

    return totalSize
  }

  showLimitWarning = () => {
    const limit = formatBytes(this.attachmentsLimit, 1, false, 2)

    const message = i18n.t('email.attachment.limitWarning', { limit })
    omMessage.warning(message)
  }

  onSizeError = (curSize: number, fileLimit: number) => {
    console.log(' - onSizeError : ', curSize, fileLimit)
    this.showLimitWarning()
  }

  checkSelectFiles = async (files: FileList): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      console.log(' - checkSelectFiles : ', files)
      let selectedSize = 0
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]
        selectedSize += file.size
      }

      let sizeLimit = this.attachmentsLimit
      const totalSize = this.getTotalAttachmentSize()

      sizeLimit = sizeLimit - totalSize

      if (selectedSize > sizeLimit) {
        if (useLargeAttachment !== true) {
          this.showLimitWarning()
          resolve(false)
        } else {
          const limit = formatBytes(this.attachmentsLimit, 1, false, 2)
          confirmBase({
            title: i18n.t('editor.title.sizeLimitExceeded'),
            content: i18n.t('editor.error.sizeLimitExceeded', { limit }),
            okText: i18n.t('editor.button.sendAsLink'),
            onOk: () => {
              this.isJMAPUpload = false
              resolve(true)
            },
            onCancel: () => {
              resolve(false)
            },
          })
        }
        return
      }

      this.isJMAPUpload = true
      resolve(true)
    })
  }

  checkSizeLimit = () => {
    let sizeLimit = this.attachmentsLimit
    const totalSize = this.getTotalAttachmentSize()

    sizeLimit = sizeLimit - totalSize

    if (sizeLimit < 0) {
      this.showLimitWarning()
    }

    return sizeLimit
  }

  onChangeFile = async (file: any, _idx: number, _total: number): Promise<boolean | void> => {
    console.log(' - onChange : ', file)

    const fileNFC = {
      name: file.name?.normalize('NFC'),
      size: file.size,
      type: file.type,
    }

    const existItem = this.attachments.find((item) => {
      return equalFile(item.file, fileNFC)
    })

    const existSharingItem = this.sharingAttachments.find((item) => {
      return equalFile(item.file, fileNFC)
    })

    if (existItem != null || existSharingItem != null) {
      const message = i18n.t('editor.error.alreadyExistFile', { filename: `( ${file.name} )` })
      omMessage.warning(message)

      return true
    }

    if (useLargeAttachment === true && this.isJMAPUpload === false) {
      const attachment = this.attachmentHelper.createAttachmentForSharing(file, null, this)
      this.addSharingAttachment(attachment)

      setTimeout(() => {
        requestAnimationFrame(() => {
          attachment.checkUpload().then(() => {
            this.updateSendBtnByUploadStatus()
          })
          this.updateSendBtnByUploadStatus()
        })
      }, 500)
    } else if (this.isJMAPUpload === true) {
      const attachment = this.attachmentHelper.createAttachment(file, null, this)

      console.log('=== onChangeFile DEBUG ===')
      console.log('Original file:', file)
      console.log('Created attachment:', attachment)
      console.log('attachment.file:', attachment.file)
      console.log('attachment.originFile:', attachment.originFile)
      console.log('attachment keys:', Object.keys(attachment))
      console.log('=== END onChangeFile DEBUG ===')

      this.addNormalAttachment(attachment)

      setTimeout(() => {
        requestAnimationFrame(() => {
          attachment.checkUpload().then(() => {
            this.updateSendBtnByUploadStatus()
          })
          this.updateSendBtnByUploadStatus()
        })
      }, 500)
    }

    return true
  }

  openAttachment = (forceShare?: boolean) => {
    console.log(' - openAttachment')

    // const acceptedType = 'image/png, image/jpeg, image/bmp, image/tif';
    const acceptedType = '*/*'

    this.isJMAPUpload = true

    if (useLargeAttachment !== true) {
      const sizeLimit = this.checkSizeLimit()
      if (sizeLimit < 0) {
        return
      }

      // 마지막 인수 - multiple - 다중 선택 여부.
      openFile(
        this.onChangeFile,
        null,
        acceptedType,
        sizeLimit,
        this.onSizeError,
        true,
        this.checkSelectFiles,
      )
    } else {
      if (forceShare === true) {
        this.isJMAPUpload = false
      }

      // 마지막 인수 - multiple - 다중 선택 여부.
      openFile(
        this.onChangeFile,
        null,
        acceptedType,
        null, //sizeLimit,
        null, // this.onSizeError,
        true,
        forceShare === true ? undefined : this.checkSelectFiles,
      )
    }
  }

  onDrop = (e: React.DragEvent) => {
    if ((e.target as HTMLElement).closest('[data-radix-popper-content-wrapper]') == null) {
      e.preventDefault()
      console.log(' --- onDrop --- ', e.dataTransfer.files[0])
      if (e.dataTransfer != null) {
        const { files } = e.dataTransfer
        const fileLimit = 0
        if (fileLimit === 0 || (files.length > 0 && files.length <= fileLimit)) {
          const acceptedType = '*/*'

          if (useLargeAttachment !== true) {
            let sizeLimit = this.checkSizeLimit()
            if (sizeLimit < 0) {
              return
            }

            this.checkSelectFiles(files).then((result) => {
              if (result === false) {
                return
              }
              for (let i = 0; i < files.length; i += 1) {
                const item = files[i]

                processOpenedFile(
                  item,
                  this.onChangeFile,
                  null,
                  acceptedType,
                  sizeLimit,
                  this.onSizeError,
                  i,
                  files.length,
                )
                sizeLimit -= item.size
              }
            })
          } else {
            this.checkSelectFiles(files).then((result) => {
              if (result === false) {
                return
              }
              for (let i = 0; i < files.length; i += 1) {
                const item = files[i]

                processOpenedFile(
                  item,
                  this.onChangeFile,
                  null,
                  acceptedType,
                  null,
                  null,
                  i,
                  files.length,
                )
              }
            })
          }
        } else if (files.length > fileLimit) {
          const message = i18n.t('common.message.uploadLimit', { limit: fileLimit })
          omMessage.warning(message)
        }
      }
    }

    if (this.dragover === true) {
      this.dragover = false
      this.setState({
        dragover: false,
      })
    }
  }

  onDragOver = (e: React.DragEvent) => {
    if ((e.target as HTMLElement).closest('[data-radix-popper-content-wrapper]') == null) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  // OFM-226: 파일 드래그 감지 — ngw 는 부모 document.body, TinyMCE 는 iframe body 에서
  // dragover 를 받아 첨부 drop overlay 를 표시한다. overlay top 재계산은 매 이벤트 수행
  // (write-if-changed) — dragover 플래그는 wrapper 미진입 드래그 종료 시 true 로 고착될
  // 수 있어, false→true 전환 시 1회 계산으로는 재실행이 스킵되기 때문.
  onEditorFileDragOver = (e: DragEvent) => {
    const { types } = e.dataTransfer!
    if (types.includes('Files') !== true) {
      return
    }
    if ((e.target as HTMLElement).closest('[data-radix-popper-content-wrapper]') == null) {
      this.applyAttachOverlayTop()
      if (this.dragover !== true) {
        this.dragover = true
        this.setState({ dragover: true })
      }
    }
  }

  // OFM-226: ngw inline 에디터는 toolbar 가 .drop-target 내부(.ngw-editor-container 안)에
  // 렌더되므로 첨부 drop overlay 의 top 을 toolbar 아래(.ngw-editor 시작점)로 보정한다.
  // TinyMCE 는 .ngw-editor 가 부모 문서에 없어 no-op → CSS 기본값(top: 4px) 유지.
  applyAttachOverlayTop = () => {
    const wrapper = this.wrapperRef.current
    if (wrapper == null) {
      return
    }
    const editor = wrapper.querySelector<HTMLElement>(
      '.htmlEditor > .ngw-editor-container > .ngw-editor',
    )
    const attachFileWrapper = wrapper.querySelector<HTMLElement>('.attachFileWrapper')
    if (editor == null || attachFileWrapper == null) {
      return
    }
    // offsetTop 좌표계 전제: .ngw-editor 의 offsetParent 와 overlay 의 containing block 이
    // 모두 position: relative 인 .drop-target 이다 — 중간 요소(.htmlEditor,
    // .ngw-editor-container)에 position 이 생기면 깨진다. -3 은 .ngw-editor margin(4px)
    // 시각 보정.
    const top = `${editor.offsetTop - 3}px`
    if (attachFileWrapper.style.top !== top) {
      attachFileWrapper.style.top = top
    }
  }

  addNormalAttachment = (attachment: any) => {
    const attachmentEx = extendUI(attachment)
    this.attachments.push(attachmentEx)

    this.forceUpdate()

    this.onValuesChanged()
  }

  addSharingAttachment = (attachment: any) => {
    const attachmentEx = extendUI(attachment)
    this.sharingAttachments.push(attachmentEx)

    this.forceUpdate()

    this.onValuesChanged()
  }

  addInlineAttachment = (attachment, cid) => {
    this.inlineAttachmentMap[cid] = attachment
  }

  getInlineAttachment = (cid) => {
    return this.inlineAttachmentMap[cid]
  }

  setAllAttachments = (attachments) => {
    this.inlineAttachmentMap = {}

    const normalAttachements = []

    attachments.forEach((attachment) => {
      const attachmentEx = extendUI(attachment)
      if (attachment.file.cid != null && attachment.file.isInline === true) {
        this.inlineAttachmentMap[attachment.file.cid] = attachmentEx
      } else {
        normalAttachements.push(attachmentEx)
      }
    })

    this.attachments = normalAttachements

    this.forceUpdate()
  }

  deleteAttachment = (attachmentObj) => {
    console.log(' - deleteAttachment - ', attachmentObj)

    this.attachments = this.attachments.filter((item) => {
      return attachmentObj.compare(item) !== true
    })

    this.sharingAttachments = this.sharingAttachments.filter((item) => {
      return attachmentObj.compare(item) !== true
    })

    if (attachmentObj != null && attachmentObj.clearURL != null) {
      attachmentObj.clearURL()
    }

    this.forceUpdate()

    this.onValuesChanged()
    this.updateSendBtnByUploadStatus()
  }

  getAttachmentElem = () => {
    let totalSize = 0
    const elems = this.attachments
      .filter((attachment) => {
        totalSize += attachment.file.size
        return attachment.file.isInline !== true
      })
      .map((item) => {
        const { file } = item

        const attrs: any = {
          attachmentObj: item,
          downloadable: true,
          enableDelete: true,
          forEditor: true,
        }

        const key = file.blobId || file.name || file.id

        attrs.api = {
          setUploadCB: (cbFunc: any) => {
            this.attachmentUploadProgressMap[item.id] = cbFunc
          },
        }

        return <Attachment {...attrs} key={key} />
      })

    const totalSizeStr = formatBytes(totalSize, 1, false)
    const limitSizeStr = formatBytes(this.attachmentsLimit, 1, false, 2)

    if (elems.length > 0) {
      return (
        <div className={classNames(styles.attachmentWrapper)}>
          <img className={classNames(styles.icon)} src={propertyAttachment24} alt="attachment" />
          <div className={classNames(styles.scrollContainer)}>{elems}</div>
          <div className={classNames(styles.attachmentSize)}>
            {totalSizeStr} / {limitSizeStr}
          </div>
        </div>
      )
    }

    return null
  }

  getSharingAttachmentElem = () => {
    // let totalSize = 0
    const elems = this.sharingAttachments
      .filter((attachment) => {
        // totalSize += attachment.file.size
        return attachment.sendType === 'sharingFile'
      })
      .map((item) => {
        const attrs: any = {
          attachmentObj: item,
          downloadable: true,
          enableDelete: true,
          forEditor: true,
        }

        const key = item.uploadId || item.fileId || item.id

        attrs.api = {
          setUploadCB: (cbFunc: any) => {
            this.attachmentUploadProgressMap[item.id] = cbFunc
          },
        }

        return <Attachment {...attrs} key={key} />
      })

    // const totalSizeStr = formatBytes(totalSize, 1, false)
    // const limitSizeStr = formatBytes(this.attachmentsLimit, 1, false, 2)

    if (elems.length > 0) {
      return (
        <div className={classNames(styles.attachmentWrapper)}>
          <img className={classNames(styles.icon)} src={propertySharableLink24} alt="attachment" />
          <div className={classNames(styles.scrollContainer)}>{elems}</div>
          {/* <div className={classNames(styles.attachmentSize)}>
            {totalSizeStr} / {limitSizeStr}
          </div> */}
        </div>
      )
    }

    return null
  }

  onToolbarChange = (values, allValues) => {
    console.log(' - onToolbarChange - ', values, allValues)
    // if (this.toolbarRef.current != null) {
    //   const toolbar = this.toolbarRef.current;
    //   console.log(' - toolbar - ', toolbar);
    // }

    if (this.editorAdapter?.lifecycle.isReady() !== true) {
      return
    }

    // PR-7c: editorWindow.focus / isEmpty / editor.selection.isCollapsed / setCursorIntoNBody0 /
    // runCommand / editor.focus → adapter host / content / cursor / dispatchToolbarCommand 호출.
    this.editorAdapter.host.focus()
    if (
      this.editorAdapter.content.isEmpty() === true &&
      this.editorAdapter.cursor.isCollapsed() === true
    ) {
      this.editorAdapter.cursor.setIntoNBodyCore()
    }

    Object.entries(values).forEach(([key, value]) => {
      this.dispatchToolbarCommand(key, value)
    })
    this.editorAdapter.host.focus()
    this.editorAdapter.cursor.focus()
  }

  /**
   * PR-7c: 기존 `editorExt.ts.runCommand(editorWindow, key, value)` 의 24-case switch 를
   * 어댑터 `IEditorCommand` 호출로 풀어 쓴 thin dispatcher. React toolbar 의 키
   * (`fontname`/`bold`/…) → `EditorCommandName` 매핑은 [pr7c-transfer-inventory.md §3] 참조.
   *
   * `insertimage` 는 union 외 (어댑터 자체 picker 흐름) — `editor.image.openNativePicker()` 로 위임.
   */
  private dispatchToolbarCommand = (key: string, value: unknown): void => {
    const command = this.editorAdapter.command
    switch (key) {
      case 'fontname':
        command.run('fontFamily', value as string)
        break
      case 'fontsize':
        command.run('fontSize', value as string)
        break
      case 'bold':
        command.run('bold')
        break
      case 'italic':
        command.run('italic')
        break
      case 'underline':
        command.run('underline')
        break
      case 'strikethrough':
        command.run('strikethrough')
        break
      case 'removeformat':
        command.run('removeFormat')
        break
      case 'alignleft':
        command.run('alignLeft')
        break
      case 'aligncenter':
        command.run('alignCenter')
        break
      case 'alignright':
        command.run('alignRight')
        break
      case 'forecolor':
        command.run('foregroundColor', value as string)
        break
      case 'backcolor':
        command.run('backgroundColor', value as string)
        break
      case 'orderedlist':
        command.run('orderedList')
        break
      case 'unorderedlist':
        command.run('unorderedList')
        break
      case 'indent':
        command.run('indent')
        break
      case 'outdent':
        command.run('outdent')
        break
      case 'link':
        command.run('link')
        break
      case 'insertimage':
        this.editorAdapter.image.openNativePicker()
        break
      case 'insertstyledhr': {
        const style = typeof value === 'string' ? value : 'solid'
        command.run('horizontalRule', { style })
        break
      }
      case 'inserttable':
        command.run('insertTable', { rows: 3, columns: 3 })
        break
      case 'blockquote':
        command.run('blockquote')
        break
      case 'ltr':
        command.run('directionLTR')
        break
      case 'rtl':
        command.run('directionRTL')
        break
      case 'undo':
        command.run('undo')
        break
      case 'redo':
        command.run('redo')
        break
      default:
        break
    }
  }

  // ===========================================================================
  // PR-7a-2 — IEditorEventHandler 콜백 + 흡수된 helper 클로저
  //
  // editorExt.ts 의 NineNative literal (L393~L1218) 메서드 본체와 그 클로저 helper
  // (getBlobURL / createImageInfo / applySettings / convertDataURL / uniqueBlobId)
  // 를 EmailForm 클래스 멤버로 흡수한다. editorExt.ts 는 thin delegate 로 축소되어
  // PR-7a-3 의 NineNative setup 제거를 단순화한다.
  //
  // 본 단계에서 어댑터의 lifecycle.init 은 호출되지 않으므로 IEditorEventHandler
  // forward path 는 활성화되지 않는다. 현 동작은 iframe 의 NineNative 호출이
  // editorExt.ts 의 thin delegate 를 거쳐 본 메서드들로 진입하는 형태로 보존.
  // ===========================================================================

  private uniqueBlobId = (prefix?: string): string => {
    const id = (prefix || 'blobid') + this.blobIdCounter
    this.blobIdCounter += 1
    return id
  }

  private getBlobURL = (blob: Blob, filename: string): string => {
    // PR-7c: editorMgr.editor.editorUpload.blobCache 직접 접근 → adapter.image.attachBlob.
    // 어댑터 내부에서 blobInfo 생성 + add 까지 수행하고 { id, uri } 반환.
    const { uri } = this.editorAdapter.image.attachBlob(blob, { name: filename })
    return uri
  }

  private base64ToBlob = (base64Str: string, mime: string): File =>
    base64ToBlobFile(base64Str, mime)

  private dataURLtoBlob = (dataurl: string): File => dataUrlToBlobFile(dataurl)

  private imgToBase64 = async (
    imgTag: HTMLImageElement,
    forceImgSrc?: string,
  ): Promise<boolean> => {
    let imgSrc = forceImgSrc
    if (imgSrc == null) {
      imgSrc = imgTag.src != null && imgTag.src.trim().length > 0 ? imgTag.src : imgTag.currentSrc
    }
    if (imgSrc.indexOf('blob:') == 0) {
      return new Promise(function (resolve) {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', imgSrc!, true)
        xhr.responseType = 'blob'
        xhr.onload = function () {
          if (this.status == 200) {
            const myBlob = this.response
            const reader = new FileReader()
            reader.onloadend = function () {
              const data = reader.result
              if (data != null && (data as string).indexOf('data:') == 0) {
                imgTag.setAttribute('src', data as string)
                resolve(true)
              } else {
                console.log(' unknown image data type. ', data)
                resolve(false)
              }
            }
            reader.readAsDataURL(myBlob)
          }
        }
        xhr.onerror = function () {
          console.log(' blob onerror ')
          resolve(false)
        }
        xhr.send()
      })
    }
    return Promise.resolve(false)
  }

  private convertCid = (bodyHTML: string): string => {
    const domParser = new DOMParser()
    const doc = domParser.parseFromString(bodyHTML, 'text/html')
    const imgList = doc.querySelectorAll('img')

    imgList.forEach((img) => {
      if (img.src.toLowerCase().indexOf('cid:') === 0) {
        img.setAttribute('data-cid', img.src)
        img.src = ''
      }
    })

    const lowerText = bodyHTML.toLowerCase()
    if (lowerText.indexOf('<!doctype') > -1 || lowerText.indexOf('<!DOCTYPE') > -1) {
      return new XMLSerializer().serializeToString(doc)
    }

    return doc.body.innerHTML
  }

  /**
   * PR-N9a-1 — NgwEditor 모드 전용 cid 이미지 변환 (Document 반환).
   *
   * `@ngweditor/react@0.2.10` 의 image NodeSpec parseDOM regex (`^(?:https?:|data:image\/)`) 가
   * `cid:` src 를 schema 단계에서 drop 하므로, `convertCid` 가 만든 `<img src="" data-cid="...">`
   * 도 동일하게 drop 된다. 본 메서드는 setDataFromDOM/quotedBody.set/서명 주입 직전에 호출되어
   * cid 이미지를 **data URL 로 사전 변환** 하면서 어댑터의 `urlMap` 에 `dataUrl → origCidSrc`
   * 매핑을 등록한다. 송신 시점에는 `getDataPreprocessed()` 가 본 mapping 으로 역치환한다.
   *
   * 직렬화를 생략하고 Document 를 그대로 반환해(draft 는 setDataFromDOM, reply/resend 는
   * getSafeElement 로 직결) 대형 본문의 "직렬화 → 재파싱" 왕복(2.7MB 기준 ~1.2s)을 제거한다.
   * binary 회수는 `attachment.download()` 의 idempotent 캐시에 의존 — 답장/resend 는 이미
   * `Promise.all(promiseList)` 완료 후라 동기 hit, draft 는 본 메서드 안에서 실제 download 발생.
   */
  private convertCidNgwToDocument = (bodyHTML: string | Document): Promise<Document> =>
    convertCidImagesToDocument(
      bodyHTML,
      (cid) => this.inlineAttachmentMap[cid],
      (cidSrc, dataUrl) => this.editorAdapter.image.registerUrlMapping(cidSrc, dataUrl),
    )

  /**
   * draft-reopen-parse-share: initEditor(reopen seeding)가 보관한 파싱 Document 를 1회 소비한다.
   * 같은 본문 문자열일 때만 재사용하고(값 비교 — seeding 과 본문 적용의 detail 이 다른 경우 방어),
   * 불일치/부재 시 문자열을 그대로 반환해 호출부(convertCid)가 새로 파싱한다. 반환된 Document 는
   * cid 변환이 mutate 하고 setDataFromDOM/getSafeElement 가 소진하므로 재보관하지 않는다.
   */
  private takeReopenParsedDoc = (bodyHTML: string): string | Document => {
    const doc = this.reopenParsedDoc
    const html = this.reopenParsedHTML
    this.reopenParsedDoc = null
    this.reopenParsedHTML = null
    if (doc != null && html === bodyHTML) return doc
    return bodyHTML
  }

  // reply-editor-quoted-lazy-bake: reply/forward 인용 본문 백그라운드 bake.
  // cid→dataURL 변환과 발신자 허용 확인(네트워크)을 병렬로 겹친 뒤, time-sliced 변환
  // (getSafeHTML asyncYield — 메인 스레드 freeze 없음)으로 굽고, 완료 시 접힘 상태 +
  // undo 미기록(addToHistory:false — 사용자가 Ctrl+Z 로 주입분을 날리지 않게)으로 주입한다.
  // reply-quoted-load-before-edit: 주입은 bake 완료 즉시 실행하고, 편집(오버레이 제거)은
  // 서명∧인용 주입이 모두 끝난 수렴점에서 열린다 — 편집 중 주입 블록이 끼어드는 freeze 를
  // 없애는 대신 초기 로딩이 길어지는 트레이드오프(사용자 결정).
  private bakeQuotedBodyLazy = async (
    bodySource: string | Document,
    quotedCollapsed: boolean,
    sender: string,
    prevEmailId: string,
  ): Promise<void> => {
    // bodySource 가 Document 면 뷰어 선파싱(parsed-body-doc-from-viewer) hit — convertCid 가
    // string→DOM 파싱(2.7MB WebKit ~4s) 없이 그대로 mutate/소진한다. string 이면 기존 경로.
    this.replyPerfMark(
      `bake:start htmlLen=${typeof bodySource === 'string' ? bodySource.length : 'doc(preparsed)'}`,
    )
    const [cidDoc, isAllowed] = await Promise.all([
      this.convertCidNgwToDocument(bodySource).then((r) => {
        this.replyPerfMark('bake:convertCid:done')
        return r
      }),
      new Promise<boolean>((resolve) => {
        this.checkAllowedAndKnownSender(sender, prevEmailId, (r) => {
          this.replyPerfMark('bake:checkAllowed:done')
          resolve(r)
        })
      }),
    ])
    if (this.quotedBakeController.signal.aborted) return

    // reply-quoted-dom-direct-apply: getSafeHTML(문자열) 대신 getSafeElement(DocumentFragment)로
    // 받아 "DOM→문자열→DOM" 재파싱 왕복을 제거한다. keepRoot 로 최상위 래퍼(인용문 기준
    // 폰트/색/줄간격)를 보존하고, dirAutoMode:'preserve' 로 dir="auto" 를 편집 가능 상태로 유지한다
    // (뷰어 기본값 resolve 와 구분). cidDoc 는 getSafeElement 가 adopt 로 소진하므로 재사용 금지.
    const safeFragment = await getSafeElement(cidDoc, {
      isAllowed: () => isAllowed,
      keepRoot: true,
      dirAutoMode: 'preserve',
      signal: this.quotedBakeController.signal,
    })
    this.replyPerfMark(`bake:transform:done childCount=${safeFragment.childNodes.length}`)
    if (this.quotedBakeController.signal.aborted) return
    if (this.editorAdapter?.lifecycle.isReady() !== true) return

    this.replyPerfMark('bake:inject:start')
    this.editorAdapter.mail.region.quotedBody.set(safeFragment, {
      collapsed: quotedCollapsed,
      addToHistory: false,
    })
    this.replyPerfMark('bake:inject:done')
  }

  // 인용 bake 완료 대기 게이트 — 본문을 직렬화(getEmailValues)하는 모든 진입점
  // (callSend/callScheduleSend/callSave/callSaveLocal/handleEMLPreview)이 호출한다.
  private ensureQuotedBaked = (): Promise<void> => this.quotedBakePromise ?? Promise.resolve()

  // reply 로딩 구간 계측(perf-reply e2e 하네스 전용). window.__replyPerfEnabled 일 때만
  // performance.now 타임스탬프를 콘솔로 남긴다 — 프로덕션 빌드에선 console 제거로 무해.
  private replyPerfMark = (label: string): void => {
    if ((window as unknown as { __replyPerfEnabled?: boolean }).__replyPerfEnabled !== true) return
    console.log(`[PERF-REPLY] ${label} ${Math.round(performance.now())}`)
  }

  private convertDataURL = async (html: string): Promise<string> => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const imgList = doc.querySelectorAll('img')
    const promiseList = Array.from(imgList).map(async (img) => {
      let imgSrc = img.getAttribute('src')
      if (img.getAttribute('data-cid') != null) {
        imgSrc = img.getAttribute('data-cid')
      } else if (
        (img.getAttribute('owa-cid') != null || img.src.indexOf('blob:') == 0) &&
        img.getAttribute('data-nine-src') != null
      ) {
        imgSrc = img.getAttribute('data-nine-src')
      }

      if (imgSrc != null && imgSrc.indexOf('cid:') == 0) {
        const cid = imgSrc.substring(4)
        const attachment = this.inlineAttachmentMap[cid]
        if (attachment != null) {
          const downloadObj = await attachment.download()
          imgSrc = downloadObj.path
        }
      }

      if (imgSrc != null && imgSrc.indexOf('blob:') == 0) {
        await this.imgToBase64(img, imgSrc)
      }
    })

    await Promise.all(promiseList)

    return doc.body.innerHTML
  }

  private createImageInfo = (file: File, url: string, callbackStr?: string): any => {
    if (this.processImageMap[url] == null) {
      // PR-7c: callbackStr 은 iframe contentWindow 안의 method path (e.g. "editorMgr.foo")
      // 를 dispatch 하는 경로. 어댑터 표면으로 매핑되지 않으므로 host.getWindow() 를 거쳐 접근.
      const hostWindow: any = this.editorAdapter.lifecycle.isReady()
        ? this.editorAdapter.host.getWindow()
        : null
      const imageInfo: any = {
        infoList: Array.from({ length: 16 }, () => null),
        imgObj: new Image(),
        file,
      }

      this.processImageMap[url] = imageInfo

      imageInfo.infoList[0] = { url, blob: file }

      imageInfo.imgObj.onload = () => {
        const arr = [url]
        const str = JSON.stringify(arr)

        if (callbackStr != null && hostWindow != null) {
          const callbackArr = callbackStr.split('.')
          let funcObj: any = hostWindow
          callbackArr.forEach((item) => {
            funcObj = funcObj[item]
          })
          funcObj(str)
        }
      }

      imageInfo.imgObj.src = url

      this.srcImageMap[url] = imageInfo
    }

    return this.processImageMap[url]
  }

  /**
   * C7: 사용자의 editorFont 설정 + helper.isNewEmail() 결과로 NgwEditor 의 기본 폰트 스타일을
   * 산출. fallback 4종 (`Calibri, Arial, Helvetica, sans-serif` / `11pt` / `#000000` / `#1f497d`)
   * 유지. `setEditorContainer` 의 init 시점과 `applyEditorSettings` 의 setDefaultStyle 호출
   * 시점이 본 메서드를 공유 — `areDefaultsEqual` NoOp 가드가 동일 값 재호출 시 재마운트 차단.
   */
  private computeDefaultStyle = (): EditorDefaultStyle => {
    const { helper, editorFont } = this.props
    const { editorFontFamily, editorFontSizeString, editorFontColorReFwd, editorFontColorNew } =
      editorFont

    const fontFamily = editorFontFamily?.trim()
      ? editorFontFamily
      : 'Calibri, Arial, Helvetica, sans-serif'
    const fontSize = editorFontSizeString?.trim() ? editorFontSizeString : '11pt'
    const isNew = helper?.isNewEmail?.() === true
    let fontColor = isNew ? editorFontColorNew : editorFontColorReFwd
    if (fontColor == null || fontColor.trim().length === 0) {
      fontColor = isNew ? '#000000' : '#1f497d'
    }

    return { fontFamily, fontSize, color: fontColor }
  }

  /**
   * draft-body-style-fidelity: 캐럿(빈 선택)에 기본 글자색 stored mark 를 설정한다.
   *
   * 라이브러리 toolbar 는 커서의 `textStyle` color mark 만 읽으므로, 새로 입력하는 텍스트가 기본색
   * mark 를 갖고 커서 위치 색이 toolbar 에 반영되도록 한다. 특히 nbody 가 비어 wrap 대상이 없는
   * reply/forward/신규에서 필요하다. 기본색이 검정이면 라이브러리 기본과 동일해 불필요한 mark 를
   * 만들지 않도록 생략한다. reopen(draft/resend) 은 reopenSeededStyle.color 를, 그 외는
   * computeDefaultStyle 의 색(reply=editorFontColorReFwd 등)을 사용한다.
   */
  private seedCaretColorMark = (): void => {
    if (editorType !== 'ngw') return
    const color = (this.reopenSeededStyle ?? this.computeDefaultStyle()).color
    if (color == null) return
    const c = color.trim().toLowerCase()
    if (c.length === 0) return
    // 검정 계열은 라이브러리 기본과 동일하므로 stored mark 생략(신규 메일 불필요 mark 방지).
    // 공백 제거 후 hex/rgb/rgba/명칭 변형을 모두 커버.
    const compact = c.replace(/\s+/g, '')
    if (
      compact === '#000000' ||
      compact === '#000' ||
      compact === 'black' ||
      compact === 'rgb(0,0,0)' ||
      compact === 'rgba(0,0,0,1)'
    ) {
      return
    }
    this.editorAdapter.command.run('foregroundColor', color)
  }

  private applyEditorSettings = (): void => {
    if (this.editorAdapter.lifecycle.isReady() !== true) return

    const { showOriginalMessage, alwaysRequestReadReceipt } = this.props

    // C7: 폰트 계산 단일 소스 — `computeDefaultStyle` 호출. setEditorContainer 의 init 단계에서
    // 동일 값이 이미 적용됐다면 `areDefaultsEqual` NoOp 가드가 재마운트를 차단.
    // draft-body-style-fidelity: draft/resend 재열람은 init 에 원본 body style 을 시딩했으므로
    // (reopenSeededStyle) 그 값을 재사용한다. resend 경로는 draftEmail==null 이라 본 함수가 호출되는데,
    // computeDefaultStyle(사용자 설정)로 덮으면 시딩이 무효화(+ 재마운트)되므로 seeded 값을 우선한다.
    this.editorAdapter.content.setDefaultStyle(this.reopenSeededStyle ?? this.computeDefaultStyle())

    if (showOriginalMessage === true) {
      // PR-7b: skipShowQuotedButton → mail.region.quotedButton.skip(true).
      // 원본 글로벌은 무인자 호출 시 `(use != false)` 로 true 처리. 어댑터 표면은 명시 boolean.
      this.editorAdapter.mail.region.quotedButton.skip(true)
    }

    if (alwaysRequestReadReceipt === true) {
      this.setState({ readReceiptEnabled: true })
    }

    // PR-7c: editorWindow.setCursorIntoNBody0 → adapter.cursor.setIntoNBodyCore.
    this.editorAdapter.cursor.setIntoNBodyCore()

    // PR-7b: resetChangedState → state.resetChangedState.
    this.editorAdapter.state.resetChangedState()
  }

  // === IEditorEventHandler 콜백 ===

  onReady = (): void => {
    console.log(' --- onReady')
    this.replyPerfMark('editor:onReady')
    if (this.editorAdapter.lifecycle.isReady() !== true) return

    // PR-7b: editorMgr.setNoInputTime → state.setNoInputTime.
    this.editorAdapter.state.setNoInputTime(10 * 1000)
    this.applyLanguage()
    searchHelper.loadSearchData()

    if (this.isEditorInited !== true) {
      this.isEditorInited = true

      // PR-7b: editorConfig.acex.matches = null → mail.resetAcexMatches.
      this.editorAdapter.mail.resetAcexMatches()

      // PR-7c: editorWindow.document.body.addEventListener → adapter.host.getDocument().body.
      // OFM-226: named handler(onEditorFileDragOver) + 등록 대상 캐시로 전환 — unmount 시
      // 명시 해제 (componentWillUnmount 참조).
      const editorBody = this.editorAdapter.host.getDocument().body
      editorBody.addEventListener('dragover', this.onEditorFileDragOver)
      this.dragOverListenTarget = editorBody
    }

    // PR-7b: setShowQuotedButtonText → mail.region.quotedButton.setText.
    this.editorAdapter.mail.region.quotedButton.setText(i18n.t('editor.button.showQuoted'))

    // docs/editor/mail-body-structure.md §1 — 신규/reply/forward 공통: PM doc 에 nbody region
    // 슬롯을 ensure. 후속 setSignature / setQuotedBody / setData 등은 별개 region 슬롯이라
    // 충돌하지 않으며, nbody 가 있으면 cursor.setIntoNBodyCore() 도 정상 동작한다.
    this.replyPerfMark('nbody:set:start')
    this.editorAdapter.mail.region.nBody.set('<p></p>')
    this.replyPerfMark('nbody:set:done')

    const { helper, signatureForNew, signatureForReplyForward, showOriginalMessage } = this.props
    // 인용(quoted) 영역 초기 접힘 상태는 설정값 showOriginalMessage 에 따른다.
    // 원본 메시지 표시=ON → 펼침(collapsed:false), OFF → 접힘(collapsed:true).
    const quotedCollapsed = showOriginalMessage !== true
    // PR-N10 (0.2.11): /edit 라우트 등 helper 미주입 진입점에서 후속 region.set / getPrevEmail /
    // getDraftEmail 호출이 crash 하지 않도록 early return. 가이드 §3.4-C 참조.
    if (helper == null) {
      console.warn('[EmailForm] helper === undefined — /edit route 추정. onReady 후속 로직 건너뜀.')
      return
    }
    // Sentry breadcrumb — 4-region nine-mail-id 부착 / DOMSerializer 회귀 진단.
    try {
      const editorDoc = this.editorAdapter.host.getDocument?.() ?? null
      const ngwContainer = editorDoc?.querySelector?.('.ngw-editor-container') ?? null
      const nineMailId = typeof helper.getNineMailId === 'function' ? helper.getNineMailId() : null
      Sentry.addBreadcrumb({
        category: 'editor.lifecycle',
        message: 'NgwEditor onReady (EmailForm)',
        level: 'info',
        data: {
          nineMailId,
          editorDocFound: editorDoc != null,
          ngwContainerFound: ngwContainer != null,
          nineMailIdSet: ngwContainer?.getAttribute?.('nine-mail-id') === nineMailId,
        },
      })
    } catch {
      // breadcrumb 자체는 진단 신호로 init 흐름을 깨뜨리지 않는다.
    }
    // reply-editor-quoted-lazy-bake: onReady 재진입(연속 reply, StrictMode 재마운트 등) 시
    // 이전 인용 bake 를 중단하고 controller 를 재무장한다 — abort 된 controller 로는 이후
    // 모든 bake 가 즉시 AbortError 로 끝나기 때문(EmailView bakeController 재생성 선례).
    this.quotedBakeController.abort()
    this.quotedBakeController = new AbortController()
    this.quotedBakePromise = null

    const prevEmail = helper.getPrevEmail()

    if (prevEmail != null) {
      this.replyPerfMark('reqDetail:start')
      prevEmail
        .reqDetail()
        .then((detail: any) => {
          console.log(' - prev email details - ', detail)
          this.replyPerfMark('reqDetail:done')

          const { bodyPart, attachments } = detail

          prevEmail.generateQHeadStr().then((qHeadStr: string) => {
            console.log('[DEBUG quotedHeader.set]', qHeadStr)
            // PR-7b: setQuotedHeader → mail.region.quotedHeader.set.
            // addToHistory:false — generateQHeadStr 가 비동기라 onLoaded 의 undo 리셋 선실행
            // 보다 늦게 도착할 수 있다. 초기 콘텐츠가 undo 에 기록되는 역전 방지.
            this.editorAdapter.mail.region.quotedHeader.set(qHeadStr, {
              collapsed: quotedCollapsed,
              addToHistory: false,
            })
            this.replyPerfMark('quotedHeader:set:done')
          })

          let isHTML = false
          let bodyHTML = ''

          if (bodyPart != null) {
            isHTML = bodyPart.hasHTMLBody() === true
            bodyHTML = bodyPart.getHTMLBody()
          }

          const promiseList: Promise<any>[] = []

          const allAttachments = [...(attachments || [])]
          if (bodyPart != null && bodyPart.files != null && bodyPart.files.length > 0) {
            bodyPart.files.forEach((file) => {
              const existItem = allAttachments.find((attachment) => {
                return attachment.blobId === file.blobId
              })
              if (existItem == null) {
                allAttachments.push(file)
              }
            })
          }

          if (allAttachments != null && allAttachments.length > 0) {
            const attachmentList = allAttachments
              .filter((file: any) => {
                let isInline = isHTML === true && file.cid != null
                if (isInline === true) {
                  isInline = bodyHTML.indexOf(file.cid) > -1
                }
                file.isInline = isInline

                if (helper.cmd === 'forward') {
                  return true
                }

                return isInline === true
              })
              .map((file: any) => {
                const attachment = this.attachmentHelper.createAttachmentFromDraft(file, this)
                if (file.isInline === true) {
                  promiseList.push(attachment.download())
                }
                return attachment
              })
            this.setAllAttachments(attachmentList)
          }

          // reply-editor-quoted-lazy-bake Phase 5: 입력 준비(undo 리셋 + 캐럿 선배치)를 즉시
          // 실행한다. 이 시점 doc 는 nbody(+인용헤더)뿐이라 리셋이 싸고, 이후 도착하는 서명/
          // 인용헤더/인용본문은 전부 addToHistory:false 로 주입되므로 리셋 선실행이 "초기
          // 콘텐츠가 undo 에 기록되는" 역전을 만들지 않는다(주입 후 거대 doc 위 재리셋 ~1.1s
          // 회피). 서명 promise 를 반환해 호출부가 인용 bake 와 수렴시킨다.
          const onLoaded = (): Promise<void> => {
            this.replyPerfMark('onLoaded:start')
            this.editorAdapter.state.resetUndoHistory()
            this.placeInitialFocus()
            return this.setSignature(signatureForReplyForward, { addToHistory: false })
          }

          // reply-quoted-load-before-edit: 수렴점 — 서명∧인용 주입이 모두 끝난 뒤에야
          // initFocus(saveFormValues baseline + 오버레이 제거 + 포커스 라우팅)로 편집을 연다.
          // 편집 중 주입 블록(~3.5s)이 끼어드는 freeze 를 없애는 대신 초기 로딩을 기다리는
          // 트레이드오프(사용자 결정). 오버레이가 걷히는 순간 인용 토글 버튼도 이미 존재한다.
          const openForEdit = (signatureReady: Promise<void>, quotedReady: Promise<void>) => {
            void Promise.allSettled([signatureReady, quotedReady]).then(() => {
              if (this.isUnmounted) return
              this.replyPerfMark('editor:inputReady')
              this.initFocus()
            })
          }

          const setQuotedBody = () => {
            if (bodyPart != null) {
              if (isHTML === true) {
                const sender = prevEmail.data.from[0].email

                if (editorType === 'ngw') {
                  // reply-editor-quoted-lazy-bake: 인용 bake(원문 inline-style 변환 + PM 주입)는
                  // 서명과 병렬로 진행하고(time-sliced 변환), send/save/EML preview 는
                  // ensureQuotedBaked() 로 완료를 기다려 인용 누락을 막는다.
                  // parsed-body-doc-from-viewer: 뷰어 선파싱 Document 가 있으면(원문 완전
                  // 일치 검증 — getHTMLBody 는 매 호출 재조립되는 비캐시 문자열이라 값 비교이며,
                  // 이 === 가드가 가공 경로 비대칭의 최종 안전망) convertCid 의 전체 파싱(~4s)
                  // 을 생략한다. 앞선 inline 첨부 필터(bodyHTML.indexOf)는 문자열을 그대로
                  // 쓰므로 take 는 이 지점(bake 직전)에서 수행한다. miss 는 기존 문자열 경로.
                  const quotedViewerDoc = BODY_DOC_FROM_VIEWER
                    ? takeParsedBodyDocument(prevEmail.data.id, bodyHTML)
                    : null
                  const signatureReady = onLoaded()
                  this.quotedBakePromise = this.bakeQuotedBodyLazy(
                    quotedViewerDoc ?? bodyHTML,
                    quotedCollapsed,
                    sender,
                    prevEmail.data.id,
                  )
                    .catch((err: unknown) => {
                      // 중단(폼 닫기/재진입)은 정상 흐름. 그 외 실패는 표면화하되 promise 는
                      // 정리해 send/save 가 영구 블록되지 않게 한다(인용 없는 상태로 진행).
                      if ((err as { name?: string })?.name === 'AbortError') return
                      console.error(' - quoted bake error - ', err)
                      Sentry.captureException(err)
                      omMessage.error(i18n.t('editor.error.quotedLoadFailed'))
                    })
                    .finally(() => {
                      this.quotedBakePromise = null
                    })
                  openForEdit(signatureReady, this.quotedBakePromise)
                  return
                }

                // TinyMCE legacy 경로 — 기존 eager 동작 유지(editorType 고정 'ngw' 라 현재 미사용).
                const convertPromise: Promise<string> = Promise.resolve(this.convertCid(bodyHTML))

                convertPromise.then((newHtml) => {
                  this.checkAllowedAndKnownSender(sender, prevEmail.data.id, (isAllowed) => {
                    getSafeHTML(newHtml, { isAllowed: () => isAllowed }).then(
                      (safeHTML: string) => {
                        // PR-7b: setQuotedBody → mail.region.quotedBody.set.
                        this.editorAdapter.mail.region.quotedBody.set(safeHTML, {
                          collapsed: quotedCollapsed,
                        })
                        // 인용은 이미 동기 주입 완료 — 서명만 수렴 대상.
                        openForEdit(onLoaded(), Promise.resolve())
                      },
                    )
                  })
                })
              } else {
                const bodyText = bodyPart.getTextBody()
                const bodyTextToHTML = textToHTML(bodyText)
                // PR-7b: setQuotedBody → mail.region.quotedBody.set.
                this.editorAdapter.mail.region.quotedBody.set(bodyTextToHTML, {
                  collapsed: quotedCollapsed,
                })
                openForEdit(onLoaded(), Promise.resolve())
              }
            } else {
              openForEdit(onLoaded(), Promise.resolve())
            }
          }

          if (promiseList.length > 0) {
            this.replyPerfMark(`inlineDownloads:start n=${promiseList.length}`)
            Promise.all(promiseList).then((downloadObjList) => {
              this.replyPerfMark('inlineDownloads:done')
              const cidMap: { [key: string]: string } = {}
              downloadObjList.forEach((downloadObj) => {
                if (downloadObj == null) return
                const { cid } = downloadObj.file
                const cidKey = `cid:${cid}`
                cidMap[cidKey] = downloadObj.path
              })

              console.log(' - cidMap - ', cidMap)

              // PR-7b: editorMgr.cidMap = Record → mail.setCidMap(Map).
              this.editorAdapter.mail.setCidMap(new Map(Object.entries(cidMap)))
              setQuotedBody()
            })
          } else {
            setQuotedBody()
          }
        })
        .catch((err: any) => {
          console.error(' - prevEmail.reqDetail error - ', err)
        })
    }

    const draftEmail = helper.getDraftEmail()
    if (draftEmail != null) {
      draftEmail
        .reqDetail()
        .then((detail: any) => {
          console.log(' - draftEmail details - ', detail)
          const { bodyPart, subject, from, cc, bcc, to, attachments, headers } = detail

          const values: any = { subject }
          if (from != null) values.from = from
          if (cc != null) values.cc = cc
          if (bcc != null) values.bcc = bcc
          if (to != null) values.to = to

          this.setFormValues(values)

          this.checkSecureSendHeader(headers)
          this.checkReadReceiptHeader(headers)

          const allAttachments = [...(attachments || [])]
          if (bodyPart != null && bodyPart.files != null && bodyPart.files.length > 0) {
            bodyPart.files.forEach((file) => {
              const existItem = allAttachments.find((attachment) => {
                return attachment.blobId === file.blobId
              })
              if (existItem == null) {
                allAttachments.push(file)
              }
            })
          }

          if (allAttachments != null && allAttachments.length > 0) {
            const attachmentList = allAttachments.map((file: any) => {
              return this.attachmentHelper.createAttachmentFromDraft(file, this)
            })
            this.setAllAttachments(attachmentList)
          }

          const onLoaded = () => {
            // PR-7b: resetUndoHistory → state.resetUndoHistory.
            this.editorAdapter.state.resetUndoHistory()
            this.initFocus()
          }

          if (bodyPart != null) {
            if (bodyPart.hasHTMLBody() === true) {
              const bodyHTML = bodyPart.getHTMLBody()

              if (this.iframe != null) {
                this.iframe.style.visibility = 'hidden'
              }

              const onSetDataDone = () => {
                if (this.iframe != null) {
                  this.iframe.style.visibility = 'visible'
                }
                onLoaded()
              }

              if (editorType === 'ngw') {
                // draft-setdata-dom-normalize: convertCidNgwToDocument(Document) → setDataFromDOM 으로
                // 재파싱(~8회→2회) 제거. reopenBodyColor 승격(draft-body-style-fidelity)은
                // setDataFromDOM 내부 정규화 체인 맨 앞(DOMPurify 전)에서 수행 — 별도 wrap 호출 제거.
                // draft 경로는 Promise.all(promiseList) 흐름이 없어 convertCidNgwToDocument 안에서 매
                // cid 마다 attachment.download() 를 await 한다(idempotent 캐시로 안전).
                this.convertCidNgwToDocument(this.takeReopenParsedDoc(bodyHTML))
                  .then((cidDoc) => {
                    if (this.isUnmounted) return
                    return this.editorAdapter.content.setDataFromDOM!(cidDoc, {
                      resetUndoHistory: true,
                      restoreFold: true,
                      reopenBodyColor: this.reopenBodyColor ?? undefined,
                    }).then(() => {
                      if (this.isUnmounted) return
                      onSetDataDone()
                    })
                  })
                  .catch((err: unknown) => {
                    // cid 변환/본문 주입 실패 시 로딩 overlay(iframe hidden)가 영구 잔존하지
                    // 않도록 표면화 후 onSetDataDone 으로 해제한다. reply 경로(bakeQuotedBodyLazy)의
                    // .catch 와 동일한 방어 패턴.
                    console.error(' - draft body load error - ', err)
                    Sentry.captureException(err)
                    if (this.isUnmounted) return
                    omMessage.error(i18n.t('editor.error.invalidData'))
                    onSetDataDone()
                  })
              } else {
                // TinyMCE 경로(제거 예정) — 기존 문자열 setData 유지.
                const newHtml = this.convertCid(bodyHTML)
                const finalHtml =
                  this.reopenBodyColor != null
                    ? wrapRegionTextWithDefaultColor(newHtml, this.reopenBodyColor)
                    : newHtml
                this.editorAdapter.content
                  .setData(finalHtml, { resetUndoHistory: true, restoreFold: true })
                  .then(onSetDataDone)
              }
            } else {
              const bodyText = bodyPart.getTextBody()
              // setData 는 입력을 HTML 로 취급하므로(text/plain draft 의 \n 이 공백으로 접힘),
              // reply/forward/resend 경로와 동일하게 textToHTML 로 이스케이프 + <br /> 변환.
              const bodyTextToHTML = textToHTML(bodyText)
              // PR-7b: setData → content.setData.
              this.editorAdapter.content.setData(bodyTextToHTML)
              onLoaded()
            }
          } else {
            onLoaded()
          }
        })
        .catch((err: any) => {
          console.error(' - draftEmail.reqDetail error - ', err)
        })
    } else {
      this.applyEditorSettings()
    }

    const extValues = helper.getExtValues()

    const resendEmail =
      extValues != null && extValues.resendEmail != null ? extValues.resendEmail : null

    if (resendEmail != null) {
      resendEmail
        .reqDetail()
        .then((detail: any) => {
          console.log(' - resendEmail details - ', detail)

          const { bodyPart, attachments } = detail

          let isHTML = false
          let bodyHTML = ''

          if (bodyPart != null) {
            isHTML = bodyPart.hasHTMLBody() === true
            bodyHTML = bodyPart.getHTMLBody()
          }

          const promiseList: Promise<any>[] = []

          const allAttachments = [...(attachments || [])]
          if (bodyPart != null && bodyPart.files != null && bodyPart.files.length > 0) {
            bodyPart.files.forEach((file) => {
              const existItem = allAttachments.find((attachment) => {
                return attachment.blobId === file.blobId
              })
              if (existItem == null) {
                allAttachments.push(file)
              }
            })
          }

          if (allAttachments != null && allAttachments.length > 0) {
            const attachmentList = allAttachments.map((file: any) => {
              const attachment = this.attachmentHelper.createAttachmentFromDraft(file, this)
              if (file.isInline === true) {
                promiseList.push(attachment.download())
              }
              return attachment
            })
            this.setAllAttachments(attachmentList)
          }

          const onLoaded = () => {
            // PR-7b: resetUndoHistory → state.resetUndoHistory.
            this.editorAdapter.state.resetUndoHistory()
            this.initFocus()
          }

          const setData = () => {
            if (bodyPart != null) {
              if (isHTML === true) {
                const sender = resendEmail.data.from[0].email

                if (editorType === 'ngw') {
                  // draft-setdata-dom-normalize: convertCidNgwToDocument → getSafeElement(DOM 직결)
                  // → setDataFromDOM 으로 재파싱 제거. getSafeElement 옵션은 Phase 1.5 실측으로
                  // OLD getSafeHTML 과 parity 확정: dirAutoMode:'preserve'(동기 bake 기본값),
                  // keepRoot:true(최상위 래퍼 보존 — DOMPurify 가 div 를 strip 안 함). reopenBodyColor
                  // 승격은 setDataFromDOM 내부 정규화 체인 맨 앞에서 수행(별도 wrap 호출 제거).
                  // 본문 변환/bake/주입 실패 시 로딩 상태가 영구 잔존하지 않도록 각 비동기
                  // 경계에서 isUnmounted 가드 + .catch 로 표면화 후 onLoaded 로 해제한다.
                  // checkAllowedAndKnownSender 는 콜백 기반이라 내부 getSafeElement 체인에 별도
                  // .catch 를 둔다. reply 경로(bakeQuotedBodyLazy)의 .catch 와 동일한 방어 패턴.
                  const handleResendBodyError = (err: unknown) => {
                    console.error(' - resend body load error - ', err)
                    Sentry.captureException(err)
                    if (this.isUnmounted) return
                    omMessage.error(i18n.t('editor.error.invalidData'))
                    onLoaded()
                  }
                  this.convertCidNgwToDocument(this.takeReopenParsedDoc(bodyHTML))
                    .then((cidDoc) => {
                      if (this.isUnmounted) return
                      this.checkAllowedAndKnownSender(sender, resendEmail.data.id, (isAllowed) => {
                        if (this.isUnmounted) return
                        getSafeElement(cidDoc, {
                          isAllowed: () => isAllowed,
                          dirAutoMode: 'preserve',
                          keepRoot: true,
                        })
                          .then((safeFragment) => {
                            if (this.isUnmounted) return
                            return this.editorAdapter.content.setDataFromDOM!(safeFragment, {
                              reopenBodyColor: this.reopenBodyColor ?? undefined,
                            }).then(() => {
                              if (this.isUnmounted) return
                              onLoaded()
                            })
                          })
                          .catch(handleResendBodyError)
                      })
                    })
                    .catch(handleResendBodyError)
                } else {
                  // TinyMCE 경로(제거 예정) — 기존 문자열 getSafeHTML + setData 유지.
                  const newHtml = this.convertCid(bodyHTML)
                  this.checkAllowedAndKnownSender(sender, resendEmail.data.id, (isAllowed) => {
                    getSafeHTML(newHtml, { isAllowed: () => isAllowed }).then(
                      (safeHTML: string) => {
                        const finalHtml =
                          this.reopenBodyColor != null
                            ? wrapRegionTextWithDefaultColor(safeHTML, this.reopenBodyColor)
                            : safeHTML
                        this.editorAdapter.content.setData(finalHtml)
                        onLoaded()
                      },
                    )
                  })
                }
              } else {
                const bodyText = bodyPart.getTextBody()
                const bodyTextToHTML = textToHTML(bodyText)
                // PR-7b: setData → content.setData.
                this.editorAdapter.content.setData(bodyTextToHTML)
                onLoaded()
              }
            } else {
              onLoaded()
            }
          }

          if (promiseList.length > 0) {
            this.replyPerfMark(`inlineDownloads:start n=${promiseList.length}`)
            Promise.all(promiseList).then((downloadObjList) => {
              this.replyPerfMark('inlineDownloads:done')
              const cidMap: { [key: string]: string } = {}
              downloadObjList.forEach((downloadObj) => {
                if (downloadObj == null) return
                const { cid } = downloadObj.file
                const cidKey = `cid:${cid}`
                cidMap[cidKey] = downloadObj.path
              })

              console.log(' - cidMap - ', cidMap)

              // PR-7b: editorMgr.cidMap = Record → mail.setCidMap(Map).
              this.editorAdapter.mail.setCidMap(new Map(Object.entries(cidMap)))
              setData()
            })
          } else {
            setData()
          }
        })
        .catch((err: any) => {
          console.error(' - resendEmail.reqDetail error - ', err)
        })
    }

    if (draftEmail == null && prevEmail == null && resendEmail == null) {
      this.setSignature(signatureForNew, { addToHistory: false }).then(() => {
        // PR-7b: resetUndoHistory → state.resetUndoHistory.
        this.editorAdapter.state.resetUndoHistory()
        this.initFocus()
      })
    }

    if (extValues != null && resendEmail == null) {
      const prevValues = this.getFormValues()
      const newValues = { ...prevValues, ...extValues }

      if (extValues.attachmentFiles != null) {
        extValues.attachmentFiles.forEach((file: File) => {
          const attachment = this.attachmentHelper.createAttachment(file, null, this)
          this.addNormalAttachment(attachment)
        })

        delete newValues.attachmentFiles
      }

      this.setFormValues(newValues)
    }

    // PR-7b: editorMgr.checkData() → mail.checkData().
    this.editorAdapter.mail.checkData()
  }

  onSetDataEnd = (): void => {
    console.log(' --- onSetDataEnd')
  }

  notifyNoInput = (): void => {
    console.log(' --- notifyNoInput - need Save!!')
    this.callSave()
  }

  onScrollChanged = (top: number, bottom: number): void => {
    console.log(' --- setScrollPos : ', top, bottom)
    // PR-7c: editorWindow.innerHeight / scrollBy → adapter.host.getWindow() 경유.
    if (this.editorAdapter.lifecycle.isReady() !== true) return
    const hostWindow = this.editorAdapter.host.getWindow()
    const h = hostWindow.innerHeight
    if (top < 0) {
      hostWindow.scrollBy(0, top - h / 2)
    } else if (bottom > h) {
      hostWindow.scrollBy(0, bottom - h + 16)
    }
  }

  editorHeightChanged = (scrollHeight: number, diff: number): void => {
    console.log(' --- editorHeightChanged : ', scrollHeight, diff)
  }

  hasNativeToolbar = (): HasNativeToolbarResult => {
    console.log(' --- hasNativeToolbar')
    return 'props'
  }

  toolbarPropsChanged = (payload: ToolbarPropsChangePayload): void => {
    this.onToolbarPropChanged(payload.formattedString, payload.buttonString)
  }

  showCreateLinkView = (url: string, text: string, type: string): void => {
    if (this.editorAdapter.lifecycle.isReady() !== true) return
    editorToolbar.insertLink(url, text, type, {
      addLink: (linkUrl: string, linkText: string) => {
        // PR-7c: editorWindow.focus / editor.focus → adapter host.focus / cursor.focus.
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.focus()
        const isHTML = type !== 'text'
        // PR-7b: editorMgr.setLinkEx → state.setLink.
        // 프로토콜 미지정 URL 은 라이브러리 setLink(withDefaultProtocol)가 https:// 를 자동 부여한다.
        this.editorAdapter.state.setLink(linkUrl, linkText, isHTML)
        this.editorAdapter.host.focus()
      },
      removeLink: () => {
        // PR-7c: editorWindow.focus / editor.focus / execCommand('unlink') →
        //         adapter host.focus / cursor.focus / command.run('unlink').
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.focus()
        this.editorAdapter.command.run('unlink')
      },
      focus: () => {
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.focus()
      },
    })
  }

  openImageChooser = (callbackKey?: string): void => {
    console.log(' - openImageChooser : ', callbackKey)

    const onSizeError = (curSize: number, filelimit: number) => {
      console.log(' - onSizeError : ', curSize, filelimit)
      const limit = formatBytes(this.attachmentsLimit, 1, false, 2)
      const message = i18n.t('email.attachment.limitWarning', { limit })
      omMessage.warning(message)
    }

    const acceptedType = 'image/png, image/jpeg, image/bmp'

    const totalSize = this.getTotalAttachmentSize()
    const sizeLimit = this.attachmentsLimit - totalSize

    const onChange = async (file: File, _idx: number, _total: number): Promise<void> => {
      console.log(' - onChange : ', file)
      if (editorType === 'ngw') {
        // PR-N9a-1 — NgwEditor schema 의 parseDOM regex 가 blob URL 을 drop 하므로
        // attachBlobAsDataUrl 로 data URL 변환 후 PM 에 직접 insert.
        // srcImageMap key 는 originalBlobUrl 로 유지 — onPreProcess 핸들러의 blob: 매칭 호환.
        const { uri, originalBlobUrl } = await this.editorAdapter.image.attachBlobAsDataUrl(file, {
          name: file.name,
        })
        this.createImageInfo(file, originalBlobUrl, callbackKey)
        this.editorAdapter.image.insert(uri)
        return
      }
      const url = this.getBlobURL(file, file.name)
      console.log(' - onChange url : ', url)
      this.createImageInfo(file, url, callbackKey)
    }

    openFile(onChange, null, acceptedType, sizeLimit, onSizeError, false, undefined)
  }

  getProcessedImage = (
    src: string,
    size: number,
    flip: number,
    rotate: number,
  ): Promise<string> => {
    console.log(' - getProcessedImage : ', src, size, flip, rotate)

    const imgInfo = this.processImageMap[src]
    const { imgObj, infoList, file } = imgInfo
    const imgIdx = rotate * 4 + flip

    const subImgInfo = infoList[imgIdx]
    if (subImgInfo == null) {
      return new Promise<string>((resolve) => {
        const canvas = document.createElement('canvas')
        const canvasContext = canvas.getContext('2d')!

        const nw = imgObj.naturalWidth
        const nh = imgObj.naturalHeight

        let width = Math.max(nw, nh)
        let height = width

        let top = 0
        let left = 0
        if (nw > nh) {
          top = (nw - nh) / 2
        } else {
          left = (nh - nw) / 2
        }

        canvas.width = width
        canvas.height = height

        const degrees = rotate * 90
        const flipH = (flip >> 1) & 1
        const flipV = (flip >> 0) & 1
        const scaleH = flipH ? -1 : 1
        const scaleV = flipV ? -1 : 1
        const posX = flipH ? width * -1 : 0
        const posY = flipV ? height * -1 : 0

        canvasContext.save()

        if (rotate === 1 || rotate === 3) {
          canvasContext.translate(-top, -left)
          width = nh
          height = nw
        } else {
          canvasContext.translate(-left, -top)
          width = nw
          height = nh
        }

        canvasContext.translate(canvas.width / 2, canvas.height / 2)
        canvasContext.rotate((degrees * Math.PI) / 180)
        canvasContext.translate(-canvas.width / 2, -canvas.height / 2)
        canvasContext.scale(scaleH, scaleV)
        canvasContext.drawImage(imgObj, posX + left, posY + top, nw, nh)
        canvasContext.restore()

        const imgData = canvasContext.getImageData(0, 0, width, height)

        canvas.width = width
        canvas.height = height

        canvasContext.putImageData(imgData, 0, 0)

        canvas.toBlob(
          (blob) => {
            console.log('toBlob - ', blob, canvas)

            if (infoList[imgIdx] == null) {
              const pos = file.name.lastIndexOf('.')
              const ext = file.name.substring(pos + 1)
              const newName = `${file.name.substring(0, pos)}_${imgIdx}.${ext}`

              infoList[imgIdx] = {
                blob: blob!,
                url: this.getBlobURL(blob!, newName),
              }
            }

            this.srcImageMap[infoList[imgIdx]!.url] = imgInfo

            console.log(' - infoList - ', imgIdx, infoList[imgIdx])

            resolve(infoList[imgIdx]!.url)
          },
          'image/png',
          1,
        )
      })
    }

    return Promise.resolve(subImgInfo.url)
  }

  createProcessedImage = (src: string, base64Data: string, mimetype: string): string => {
    console.log(' - createProcessedImage : ', src, base64Data, mimetype)

    let blob: File
    if (base64Data.indexOf('data:') === 0) {
      blob = this.dataURLtoBlob(base64Data)
    } else {
      blob = this.base64ToBlob(base64Data, mimetype)
    }

    let fileName: string | null = null
    if (src != null && src.length > 0) {
      const url = new URL(src)
      if (url != null && url.pathname.length > 1) {
        const pos1 = url.pathname.lastIndexOf('/')
        fileName = url.pathname.substring(pos1 + 1)
      }
    }

    if (fileName == null) {
      const uniqueId = getUniqueKey('editer-img')
      fileName = `img-${uniqueId}`
    }

    const file = new File([blob], fileName, { type: mimetype })
    const url = this.getBlobURL(blob, 'noname')

    this.createImageInfo(file, url)

    return url
  }

  createProcessedImageList = (srcListStr: string): Promise<string> => {
    console.log(' - createProcessedImageList : ', srcListStr)
    const srcList = srcListStr.split('\t')

    console.log(' - srcList - ', srcList)

    const downloadList: Promise<any>[] = []

    srcList.forEach((src) => {
      if (src.indexOf('cid:') === 0) {
        const cid = src.substring(4)
        const attachment = this.getInlineAttachment(cid)
        if (attachment != null) {
          const promise = attachment.download().then((ret: any) => {
            attachment.downloadObj = ret
            return attachment
          })
          downloadList.push(promise)
        }
      }
    })

    return Promise.all(downloadList).then((retList) => {
      console.log(' - downloadList - ', retList)

      const urlList = retList.map((attachment) => {
        const ret = attachment.downloadObj
        const url = this.getBlobURL(ret.blob, ret.file.name)
        const imageInfo = this.createImageInfo(ret.file, url)
        imageInfo.attachment = attachment
        return url
      })

      return urlList.join('\t')
    })
  }

  showAvailabilityPicker = (dateTimeTableJson: string, id?: string): void => {
    console.log(' - showAvailabilityPicker : ', dateTimeTableJson, id)
    this.showSchedulingAvailabilityPicker(dateTimeTableJson, id)
  }

  /**
   * 본문 영역 paste 이벤트. viewer 에서 inline 이미지를 copy 하면 clipboard 의 text/html 안에
   * `<img src="blob:...">` 또는 `<img src="cid:...">` 가 들어온다. NgwEditor 의 PM schema 는
   * cid: / blob: src 를 parseDOM 단계에서 drop 하므로 이런 이미지는 paste 시 누락된다.
   *
   * 본 핸들러는 두 가지 fallback 으로 변환을 시도:
   *   1. clipboardData.items 에 image MIME 이 있으면 그 Blob 을 직접 사용 (가장 robust).
   *      브라우저는 viewer 에서 이미지 영역을 copy 할 때 자동으로 raw image data 도 첨부.
   *   2. 없으면 text/html 의 blob:/cid: src 를 fetch 로 변환 시도 (same-origin 한정).
   *      실패 시 src 제거 + alt 표시.
   *
   * 변환 필요 없는 경우 (data:image / https / 이미지 없음) preventDefault 하지 않고 라이브러리
   * 기본 paste 동작 유지.
   */
  onEditorPaste = (event: ClipboardEvent): void => {
    const clipboardData = event.clipboardData
    if (clipboardData == null) return

    const html = clipboardData.getData('text/html')

    // HTML 분석 — blob:/cid: img 가 있는지 확인.
    const doc = html.length > 0 ? new DOMParser().parseFromString(html, 'text/html') : null
    const imgs = doc != null ? Array.from(doc.querySelectorAll('img')) : []
    const needsConversion = imgs.some((img) => {
      const src = img.getAttribute('src') ?? ''
      return src.startsWith('blob:') || src.startsWith('cid:')
    })

    // image MIME 이 clipboardData.items 에 있는지 (브라우저 자동 첨부).
    const imageItems = Array.from(clipboardData.items).filter(
      (item) => item.kind === 'file' && item.type.startsWith('image/'),
    )

    // image item 은 "순수 이미지 붙여넣기(HTML 없음)" 일 때만 커스텀 삽입 대상으로 쓴다.
    // Excel/Word 등은 표 HTML(text/html)과 함께 표를 렌더한 image/png 를 clipboard 에 넣으므로,
    // HTML 이 있으면 image item 으로 가로채지 말고 needsConversion(본문 내 blob:/cid: img)일 때만
    // 가로챈다. 그 외에는 라이브러리 기본 paste 경로에 맡겨 표 구조 손실을 막는다.
    const needsImageItemInsert = doc == null && imageItems.length > 0

    if (!needsConversion && !needsImageItemInsert) return

    // 변환 진행 — 라이브러리 기본 paste 차단.
    event.preventDefault()
    event.stopPropagation()

    void this.convertPastedHtmlAndInsert(doc, imgs, imageItems)
  }

  /**
   * paste HTML 안의 blob:/cid: img 를 data URL 로 변환 후 PM 에 insert.
   *
   * 우선순위:
   *   1. clipboardData.items 의 image MIME blob 이 있으면 그 blob 을 data URL 로 변환 후
   *      `<img src="${dataUrl}">` HTML 로 insertContent (가장 robust)
   *   2. HTML 의 blob:/cid: img 는 fetch 로 변환 시도. 실패 시 src 제거 + alt 표시.
   *      변환된 HTML 을 insertContent 로 PM 에 직접 주입.
   *
   * 정상 변환된 img 는 onPreProcess 의 data:image/ 분기에서 cid 변환 + inline attachment 등록까지
   * 자동 진행 (dataUrlCidMap 캐싱으로 매 송신마다 같은 cid 유지).
   */
  private convertPastedHtmlAndInsert = async (
    doc: Document | null,
    imgs: HTMLImageElement[],
    imageItems: DataTransferItem[],
  ): Promise<void> => {
    // 우선순위 1 — clipboardData.items 의 image blob 직접 사용
    if (doc == null && imageItems.length > 0) {
      const promises = imageItems.map(async (item) => {
        const blob = item.getAsFile()
        if (blob == null) return ''
        try {
          const dataUrl = await blobToDataUrl(blob)
          const escaped = dataUrl.replace(/"/g, '&quot;')
          return `<img src="${escaped}" />`
        } catch {
          return ''
        }
      })
      const htmlParts = await Promise.all(promises)
      const combined = htmlParts.filter((s) => s.length > 0).join('')
      if (combined.length > 0) this.editorAdapter.content.insertContent(combined)
      return
    }

    if (doc == null) return

    // 우선순위 2 — HTML 의 cid:/blob: img 를 attachment lookup → fetch → items fallback 순으로 변환.
    // cid attachment lookup 이 우선이라 quoted body 의 cid 이미지 paste 가 same-origin fetch
    // 실패와 무관하게 동작 (2026-05-20 회귀 fix).
    await convertPastedImageSources(imgs, imageItems, {
      attachmentLookup: (cid) => this.inlineAttachmentMap[cid],
    })

    const html = doc.body.innerHTML
    if (html.length === 0) return
    this.editorAdapter.content.insertContent(html)
  }

  onCopyCut = (evt: EditorCopyCutEvent): void => {
    const { clipboardData } = evt
    if (clipboardData == null) return

    const { types } = clipboardData
    const hasHtml = types.some((type: string) => type.toLowerCase().includes('html'))
    if (!hasHtml) return

    const rawHtml = clipboardData.getData('text/html')
    if (!rawHtml) return

    if (navigator.clipboard == null) return

    const seq = ++this.copySeq

    const convertPromise = this.convertDataURL(rawHtml)
    const htmlBlobPromise: Promise<Blob> = convertPromise.then((htmlData) => {
      if (seq !== this.copySeq) return Promise.reject(new Error('copy superseded'))
      return new Blob([htmlData], { type: 'text/html' })
    })
    const textBlobPromise: Promise<Blob> = convertPromise.then((htmlData) => {
      if (seq !== this.copySeq) return Promise.reject(new Error('copy superseded'))
      return new Blob([getViewText(htmlData)], { type: 'text/plain' })
    })

    const clipboardItem = new ClipboardItem({
      'text/html': htmlBlobPromise,
      'text/plain': textBlobPromise,
    })

    navigator.clipboard.write([clipboardItem]).catch((error) => {
      if (error instanceof Error && error.message === 'copy superseded') return
      console.error('clipboard write error: ', error)
    })
  }

  onPreProcess = (evt: EditorPreProcessEvent): void => {
    if (evt.format === 'raw' && evt.get === true) {
      console.log(' ---- PreProcess 2 ---- ', evt)

      const oldInlineAttachmentMap = { ...this.inlineAttachmentMap }

      this.inlineAttachmentMap = {}

      const imgList = evt.node.querySelectorAll('img')
      imgList.forEach((img) => {
        const src = img.getAttribute('src')
        console.log(' - src - ', src, this.srcImageMap)
        if (src != null && this.srcImageMap[src] != null) {
          const imgInfo = this.srcImageMap[src]
          const { infoList, file } = imgInfo
          console.log(' - infoList - ', infoList)
          const infoIdx = infoList.findIndex((info: any) => info != null && info.url === src)
          if (infoIdx > -1) {
            const info = infoList[infoIdx]!

            if (infoIdx === 0 && imgInfo.attachment != null) {
              const { cid } = imgInfo.attachment.file
              img.src = `cid:${cid}`
              imgInfo.attachment.isUsed = true
              this.addInlineAttachment(imgInfo.attachment, cid)
            } else {
              let newName = file.name
              if (infoIdx > 0) {
                const pos = file.name.lastIndexOf('.')
                const ext = file.name.substring(pos + 1)
                newName = `${file.name.substring(0, pos)}_${infoIdx}.${ext}`
              }
              const newFile = new File([info.blob], newName, {
                lastModified: new Date().getTime(),
                type: info.blob.type,
              })

              const pos1 = src.lastIndexOf('/')
              const blobText = src.substring(pos1 + 1)

              const domain = location.host
              const cid = `${blobText}@${domain}`

              console.log(' - file - ', cid, newFile, info)

              img.src = `cid:${cid}`

              const attachment = this.attachmentHelper.createAttachment(newFile, cid, this)
              attachment.isUsed = true

              this.addInlineAttachment(attachment, cid)
            }
          }
        } else if (src != null && src.indexOf('cid:') === 0) {
          const cid = src.substring(4)
          const attachment = oldInlineAttachmentMap[cid]
          if (attachment != null) {
            attachment.isUsed = true
            this.addInlineAttachment(attachment, cid)
          }
        } else if (src != null && src.indexOf('data:image/') === 0) {
          // NgwEditor 내장 picker / paste / drag 경로로 PM 에 직접 주입된 dataUrl 이미지 처리.
          // 어댑터 urlMap / srcImageMap 어디에도 등록되지 않은 상태로 들어오므로 본 핸들러가
          // dataUrl → blob → cid 변환 + inline attachment 등록까지 책임진다.
          const { cid, attachmentFile } = resolveOrCreateDataUrlCid(src, this.editorAdapter.image, {
            host: location.host,
            createUuid: jamExt.createUuid,
          })
          img.src = `cid:${cid}`
          const attachment = this.attachmentHelper.createAttachment(attachmentFile, cid, this)
          attachment.isUsed = true
          this.addInlineAttachment(attachment, cid)
        }
      })
    }
  }

  onToolbarPropChanged = (fStr, bStr) => {
    console.log(' --- toolbarPropsChanged : ', fStr, bStr)
    const values: any = {}
    fStr.split(';').forEach((item) => {
      const [key, value] = item.split(':')
      const val = value === 'true' ? true : value === 'false' ? false : value
      values[key.toLowerCase()] = val
    })

    bStr.split(';').forEach((item) => {
      const [key, value] = item.split(':')
      const val = value === 'true' ? true : value === 'false' ? false : value
      values[key.toLowerCase()] = val
    })

    if (values.fontname != null) {
      const fontList = values.fontname.split(',')
      const trimFontList = fontList.map((item: string) => {
        return item.trim()
      })
      values.fontname = trimFontList.join(', ')
    }

    if (this.toolbarRef.current != null) {
      this.toolbarRef.current.setFieldsValue(values)
    }
  }

  createEditorToolbarElem = (): any => {
    // const toolbarElems = editorToolbar.getToolbarElems(this.curValues || {}, this)
    const toolbarElems = editorToolbar.getToolbarMenu(styles, this.curValues || {})

    if (this.curValues == null) {
      this.curValues = editorToolbar.getDefaultValues()
    }

    const formAttrs = {
      className: classNames(styles.editorToolbarForm),
      layout: 'horizontal' as const,
      ref: this.toolbarRef,
      onValuesChange: this.onToolbarChange,
      initialValues: this.curValues,
    }

    return (
      <div
        className={classNames('editorToolbar', styles.editorToolbar, styles.editorToolbarContainer)}
      >
        <OmForm {...formAttrs}>{toolbarElems}</OmForm>
      </div>
    )
  }

  setFormWrapperRef = (_ref?: any) => {
    // if (this.observerObj == null) {
    //   this.observerObj = {}
    //   this.observerObj.calcHeight = () => {
    //     if (this.observerObj.resizeTimer != null) {
    //       cancelAnimationFrame(this.observerObj.resizeTimer)
    //     }
    //     this.observerObj.resizeTimer = requestAnimationFrame(() => {
    //       if (this.observerObj.element != null && this.iframe != null) {
    //         const editorToolbarElem = document.querySelector('.editorToolbar')
    //         if (editorToolbarElem != null) {
    //           const emptyTop = editorToolbarElem.offsetTop + editorToolbarElem.offsetHeight
    //           const emptyHeight = document.body.offsetHeight - emptyTop
    //           this.iframe.style.height = `${emptyHeight}px`
    //           const container = editorToolbarElem.parentElement
    //           if (container.offsetHeight != container.scrollHeight) {
    //             const diff = container.scrollHeight - container.offsetHeight
    //             this.iframe.style.height = `${emptyHeight - diff}px`
    //           }
    //         }
    //         this.observerObj.resizeTimer = null
    //       }
    //     })
    //   }
    //   this.observerObj.observer = new ResizeObserver((e) => {
    //     // console.log(' resize o - ', e)
    //     this.observerObj.calcHeight()
    //   })
    //   this.observerObj.clear = () => {
    //     const { observer, element, resizeTimer } = this.observerObj
    //     if (element != null) {
    //       if (observer != null) {
    //         observer.disconnect()
    //         if (resizeTimer != null) {
    //           cancelAnimationFrame(resizeTimer)
    //         }
    //       }
    //     }
    //   }
    // }
    // if (this.observerObj.ref != ref || this.observerObj.element != ref) {
    //   this.observerObj.clear()
    //   this.observerObj.element = ref
    //   this.observerObj.ref = ref
    //   if (this.observerObj.element != null) {
    //     this.observerObj.calcHeight()
    //     this.observerObj.observer.observe(this.observerObj.element)
    //   }
    // }
  }

  openSelectUser = (options) => {
    const { title, key } = options

    let ref = null
    if (key === 'to') {
      ref = this.toRef
    } else if (key === 'cc') {
      ref = this.ccRef
    } else if (key === 'bcc') {
      ref = this.bccRef
    }

    const prevEmailList = ref != null && ref.current != null ? ref.current.getEmailList() : []

    selectUser({
      userOnly: false,
      title: title || '',
      disableEmails: prevEmailList,
      // excludeEmailList: prevEmailList,
      // defaultEmailList: prevEmailList,
      afterClose: () => {
        if (ref != null && ref.current != null) {
          ref.current.focus()
        }
      },
      onOk: (data, closeFunc) => {
        console.log(' -- selected user : ', data)
        // const newList = [...shareList]
        const { selectedUserList } = data

        const userInfoList = selectedUserList.map((item) => {
          const { name, email } = item
          return { name, email }
        })

        if (ref != null && ref.current != null) {
          ref.current.addUserInfoList(userInfoList)
        }

        closeFunc()
      },
    })
  }

  handleSelectionsChange = (selections: TimeRange[]) => {
    this.setState({
      selectedAvailabilityTimes: selections,
    })
  }

  handleSchedulingTimeSelect = () => {
    console.log(' - handleSchedulingTimeSelect - ')

    // Get all selected time ranges and edit mode from state
    const { selectedAvailabilityTimes, editingAvailabilityId } = this.state

    try {
      // Convert selections to SendAvailabilityItems format
      const sendAvailabilityItems = {
        items: selectedAvailabilityTimes.map((selection) => ({
          startTime: selection.start.getTime(),
          endTime: selection.end.getTime(),
        })),
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }

      if (selectedAvailabilityTimes.length == 0) {
        // ?? 없을때는 어떤 처리를 할까?
      }

      // Use OutlookDateTimeHelper to convert to JSON format (uses settings-based formatting)
      const { json } =
        OutlookDateTimeHelper.getJsonDataAndTimeZoneFromSendAvailabilityItems(sendAvailabilityItems)

      const tableTitle = i18n.t('calendar.selectAvailability.availabilityTableTitle')
      const tableDesc = i18n.t('calendar.selectAvailability.availabilityTableDesc')
      const downloadDesc = i18n.t('calendar.selectAvailability.schedulingByOfficeNEXT')
      const downloadLink = 'https://officenext.ai'

      // PR-7c: editorWindow.addSendAvailability → adapter.mail.insert.addSendAvailability (payload 4 필드 보강).
      //         setCursorIntoNBody → adapter.cursor.setIntoNBody(true).
      if (this.editorAdapter.lifecycle.isReady() === true) {
        if (editingAvailabilityId != null) {
          // Edit mode: update existing availability with the same ID
          this.editorAdapter.mail.insert.addSendAvailability({
            dateTimeTableJson: json,
            title: tableTitle,
            tableDesc,
            appDesc: downloadDesc,
            appLink: downloadLink,
            id: editingAvailabilityId,
          })

          console.log(' - Updated existing availability with ID: ', editingAvailabilityId)
        } else {
          // Create mode: add new availability (id 생략)
          this.editorAdapter.mail.insert.addSendAvailability({
            dateTimeTableJson: json,
            title: tableTitle,
            tableDesc,
            appDesc: downloadDesc,
            appLink: downloadLink,
          })

          console.log(' - Added new availability')
        }

        // Close the drawer
        this.onSelectAvailabilityClose()

        setTimeout(() => {
          this.editorAdapter.cursor.setIntoNBody(true)
        }, 500)
      }
    } catch (error) {
      console.error('Error converting availability times:', error)
      omMessage.error(i18n.t('calendar.selectAvailability.errorMessage'))
    }
  }

  handleAddAvailability = (startDate, endDate) => {
    console.log(' - handleAddAvailability - ', startDate, endDate)
  }

  /**
   * Edit select availability
   * Opens the SchedulingAssistantTab with pre-loaded selections from JSON
   *
   * @param {string} dateTimeTableJson - JSON string containing DateTimeTable data
   * @param {string} id - ID of the availability block being edited
   */
  showSchedulingAvailabilityPicker = (dateTimeTableJson, id) => {
    console.log(' - showSchedulingAvailabilityPicker - ', dateTimeTableJson, id)

    try {
      // Step 1: Parse JSON string to DateTimeTable array
      const dateTimeTables =
        OutlookDateTimeHelper.getOutlookDateTimeTablesFromJson(dateTimeTableJson)

      // Step 2: Convert DateTimeTable array to SendAvailabilityEvent array
      const sendAvailabilityEvents =
        OutlookDateTimeHelper.convertOutlookDateTimeTables2SendAvailabilityEvents(dateTimeTables)

      // Step 3: Convert SendAvailabilityEvent array (milliseconds) to multi-selection format
      // Format: [{start: Date, end: Date}, ...]
      const selections = sendAvailabilityEvents.map((event) => ({
        start: new Date(event.startTime),
        end: new Date(event.endTime),
      }))

      console.log(' - Loaded selections for edit: ', selections)

      // Step 4: Open drawer with pre-loaded selections and edit ID
      this.setState({
        isSelectAvailabilityOpen: true,
        selectedAvailabilityTimes: selections,
        editingAvailabilityId: id,
      })
    } catch (error) {
      console.error('Error loading availability for edit:', error)
      omMessage.error(i18n.t('calendar.selectAvailability.loadError'))
    }
  }

  onSelectAvailabilityClose = () => {
    this.setState({
      isSelectAvailabilityOpen: false,
      selectedAvailabilityTimes: [], // Clear selections on close
      editingAvailabilityId: null, // Reset edit mode
      schedulingNavDate: new Date(), // Reset to today when closing
    })
  }

  clearAvailability = () => {
    this.setState({
      selectedAvailabilityTimes: [],
      editingAvailabilityId: null,
    })
  }

  getAttendeesFromEmailForm = (): any[] => {
    const recipients: any[] = []

    // Add sender (from field) first
    if (this.formRef != null) {
      const formValues = this.formRef.getFieldsValue()
      if (formValues.from) {
        const senderAccount = this.sendAccountList.find(
          (account) => account.email === formValues.from,
        )
        if (senderAccount) {
          recipients.push({
            email: senderAccount.email,
            name: senderAccount.name,
            role: 'ORGANIZER',
          })
        }
      }
    }

    // Get TO recipients
    if (this.toRef.current != null) {
      const toList = this.toRef.current.getValidUserInfoList(false)
      if (toList && toList.length > 0) {
        // from(ORGANIZER) 이 to 에도 포함된 경우 AttendeeStatusList 의 key={attendee.email}
        // 중복으로 React warning 발생 — ORGANIZER role 정보를 보존하기 위해 from 을 우선
        // 두고 to 에서 동일 email 을 제거.
        const fromEmail = recipients[0]?.email
        const dedupedToList = fromEmail ? toList.filter((u: any) => u.email !== fromEmail) : toList
        recipients.push(...dedupedToList)
      }
    }

    //

    // Get CC recipients
    // if (this.ccRef.current != null) {
    //   const ccList = this.ccRef.current.getValidUserInfoList(false)
    //   if (ccList && ccList.length > 0) {
    //     recipients.push(...ccList)
    //   }
    // }

    // Optionally include BCC recipients (commented out by default)
    // if (this.bccRef.current != null) {
    //   const bccList = this.bccRef.current.getValidUserInfoList(false)
    //   if (bccList && bccList.length > 0) {
    //     recipients.push(...bccList)
    //   }
    // }

    // Check if attendees have changed by comparing emails
    const hasChanged =
      recipients.length !== this.cachedRecipients.length ||
      recipients.some((attendee, index) => {
        const cached = this.cachedRecipients[index]
        return !cached || attendee.email !== cached.email || attendee.name !== cached.name
      })

    // Only update cache if attendees have actually changed
    if (hasChanged) {
      this.cachedRecipients = recipients
    }

    //return this.cachedAttendees
    return recipients
  }

  getFormElem = (formInfo: any) => {
    if (formInfo == null) {
      return null
    }

    const items = Object.entries(formInfo.items).map(([key, value]: [string, any]) => {
      let attrs: any = {
        ...value.attrs,
      }

      if (attrs.name == null) {
        attrs.name = key
      }
      if (attrs.label == null) {
        /* i18next-extract-disable-next-line */
        attrs.label = formInfo.labelKey ? i18n.t(`${formInfo.labelKey}.${key}`) : key
      }

      if (key === 'to' || key === 'cc' || key === 'bcc') {
        const text = attrs.label
        attrs.label = (
          <OmButton
            className={classNames(styles.selectUserBtn, key)}
            variant="text"
            onClick={() => {
              this.openSelectUser({ title: text, key })
            }}
          >
            {text}
          </OmButton>
        )
      }

      if (value.beforeSetAttrs != null) {
        attrs = value.beforeSetAttrs(attrs)
      }

      const inputAttrs: any = {
        ...value.inputAttrs,
      }

      // i18next-extract-disable
      if (inputAttrs.placeholder == null) {
        inputAttrs.placeholder = formInfo.placeholderKey
          ? i18n.t(`${formInfo.placeholderKey}.${key}`)
          : ''
      }
      // i18next-extract-enable

      if (value.readOnly === true) {
        // delete attrs.name
        inputAttrs.disabled = true
      }

      let inputElem = null
      if (value.getInputElem == null) {
        const InputComp = value.input != null ? value.input : OmInput
        inputElem = (
          <OmFormItem key={key} {...attrs}>
            <InputComp {...inputAttrs} />
          </OmFormItem>
        )
      } else {
        inputElem = value.getInputElem(this.state, attrs, inputAttrs)
      }

      return inputElem
    })

    const formElem = (
      <div
        ref={(ref) => {
          this.setFormWrapperRef(ref)
        }}
        className={classNames(styles.formWrapper)}
      >
        <OmForm {...formInfo.formAttrs}>
          {items}
          {/*
          <div className="form-actions">
            <OmButton type="primary" className="mr-2" htmlType="submit">
              {i18n.t('common.button.ok')}
            </OmButton>
            <OmButton type="default" onClick={this.onCancel}>
              {i18n.t('common.button.cancel')}
            </OmButton>
          </div>
*/}
        </OmForm>
      </div>
    )

    return formElem
  }

  onDragEnd = (_type, _e) => {
    if (this.dragover === true) {
      this.dragover = false
      this.setState({
        dragover: false,
      })
    }
  }

  render() {
    const { bccVisible, invisible, dragover, isSending, isSelectAvailabilityOpen } = this.state

    const toolbarElem = toolbar.getToolbarElem(this)
    if (this.formInfo == null) {
      this.formInfo = this.createFormInfo()
    }
    const formElem = this.getFormElem(this.formInfo)
    const attachmentElem = this.getAttachmentElem()
    const sharingAttachmentElem = this.getSharingAttachmentElem()
    const editorElem = this.getEditElem()

    const clsProps = {
      bcc: bccVisible,
    }

    return (
      <OmSpinner spinning={isSending} wrapperClassName={styles.spinWrapper}>
        <div
          ref={this.wrapperRef}
          className={classNames(styles.emailFormWrapper, 'emailFormWrapper', clsProps)}
          data-testid="mail-compose-form"
        >
          {toolbarElem}
          {formElem}
          {attachmentElem}
          {sharingAttachmentElem}
          {editorType !== 'ngw' && this.editorToolbarElem}
          <div
            className={classNames('drop-target', styles.editorWrapper, { dragover })}
            data-testid="mail-editor-wrapper"
            data-drop-target=".attachFileWrapper"
            onDrop={this.onDrop}
            onDragOver={this.onDragOver}
            onDragEnd={(e) => {
              this.onDragEnd('dragEnd', e)
            }}
            onMouseLeave={(e) => this.onDragEnd('mouseLeave', e)}
            onDragLeave={(e) => this.onDragEnd('dragLeave', e)}
          >
            <div className={classNames(styles.attachFileWrapper, 'attachFileWrapper')}>
              <div className={classNames(styles.attachFile)}>
                <img src={attachFileIcon} alt="attachFile" />
                <span>{i18n.t('editor.attachment.attachFiles')}</span>
              </div>
            </div>
            {editorElem}
          </div>
          <div className={classNames(styles.loadingAni, { show: invisible === true })}>
            <img src={syncAniGIF} alt="sync" />
          </div>
          {this.state.securityWarningVisible && (
            <div className={styles.secureBannerContainer}>
              <SecurityWarningBanner
                secureSendExpired={this.state.secureSendExpired}
                onEdit={() => this.openSecureSendPopup()}
                onDiscard={() => this.onSecureSendDiscard()}
              />
            </div>
          )}
        </div>
        <OmDrawer
          title={i18n.t('calendar.selectAvailability.proposeTimesYouAreFree')}
          closable={false}
          onClose={this.onSelectAvailabilityClose}
          open={isSelectAvailabilityOpen}
          destroyOnHidden={true}
          className={styles.schedulingAvailabilityPickerContainer}
          width={400}
          footer={
            <div className={styles.availabilityPickerFooterContent}>
              {this.getAttendeesFromEmailForm().length > 0 && (
                <AttendeeStatusList
                  attendees={this.getAttendeesFromEmailForm()}
                  busyTimes={this.state.attendeeBusyTimes}
                  selectedTimeRange={
                    this.state.selectedAvailabilityTimes.length > 0
                      ? {
                          start: this.state.selectedAvailabilityTimes[0].start,
                          end: this.state.selectedAvailabilityTimes[0].end,
                        }
                      : null
                  }
                  selectedTimeRangeList={this.state.selectedAvailabilityTimes}
                  ownerHasConflict={this.state.ownerHasConflict}
                  maxVisible={5}
                />
              )}
              <div className={styles.footer}>
                <div className={styles.right}>
                  <button
                    className={styles.cancelButton}
                    onClick={this.onSelectAvailabilityClose}
                    style={{ minWidth: 100 }}
                  >
                    {i18n.t('common.button.cancel')}
                  </button>
                  <button
                    className={`${styles.saveButton}`}
                    onClick={this.handleSchedulingTimeSelect}
                  >
                    {i18n.t('common.button.save')}
                  </button>
                </div>
              </div>
            </div>
          }
        >
          <SchedulingAssistantTab
            attendees={this.getAttendeesFromEmailForm()} // {[]}
            currentDate={this.state.schedulingNavDate}
            activeTab={'scheduling'}
            eventTitle={''}
            multiMode={true}
            initialSelections={this.state.selectedAvailabilityTimes.map((t) => ({
              ...t,
              allday: t.allday || false,
            }))}
            onSelectionsChange={this.handleSelectionsChange}
            onAttendeeBusyTimesChange={this.setAttendeeBusyTimes}
            onOwnerConflictChange={this.setOwnerHasConflict}
          />
        </OmDrawer>
      </OmSpinner>
    )
  }
}

const EmailFormWrapper = connect(mapStateToProps)(EmailForm)

export default EmailFormWrapper
